import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Upload, Download, Trash2, Edit, Save, Search, Users, LayoutGrid, X, Palette, Eye, RotateCcw, Image, AlertTriangle, Send, Crown, Clock, Sparkles, Code, BellRing, MessageSquare, Map, RefreshCcw, Heart, Sun, Moon, Mail, BarChart3 } from 'lucide-react';
import { FloorPlanCanvas, TableLibrary, TablePropertiesPanel, CustomPiecePropertiesPanel, FloorPlanPreview, CustomDragLayer, FloorPlanStylePanel, TextBoxPropertiesPanel } from '../components/FloorPlan';
import LoveGallery from '../components/Wishes/LoveGallery';
import MomentsGallery from '../components/MomentsGallery';
import RevealTab from '../components/RevealTab';
import CollectedMomentsView from '../components/CollectedMomentsView';
import RescueBanner from '../components/RescueBanner';
import RSVPEditorTab from '../components/RSVPEditorTab';
import RSVPDashboard from '../components/RSVPDashboard';
import LayoutVersionPanel from '../components/editor/LayoutVersionPanel';
import OptimizeAllImagesPanel from '../components/editor/OptimizeAllImagesPanel';
import ReviewLinkPanel from '../components/editor/ReviewLinkPanel';
import EventLinksPanel from '../components/events/EventLinksPanel';

// Custom Support Chat Icon
const SupportIcon = ({ className = "h-6 w-6", color = "#D4AF37" }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    {/* Left speech bubble with text lines */}
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="13" x2="14" y2="13" />
  </svg>
);
import { colorThemes, themeOptions, getTheme, defaultCustomColors, buildCustomTheme, hasGoodContrast } from '../config/colorThemes';
import { ColorPicker } from '../components/ui/color-picker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ─── Voice Recorder for Guest Notes ──────────────────────────────
function VoiceRecorder({
  guestForm, setGuestForm, editingGuest, eventId,
  isRecording, setIsRecording, recordedBlob, setRecordedBlob,
  recordingTime, setRecordingTime, isUploadingVoice, setIsUploadingVoice,
  mediaRecorderRef, recordingTimerRef, chunksRef,
}) {
  const audioPreviewRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const hasExistingVoice = !!guestForm.voice_note_url;
  const hasNewRecording = !!recordedBlob;

  const isTouchDevice = 'ontouchstart' in window;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        setRecordedBlob(blob);
        clearInterval(recordingTimerRef.current);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => {
        if (t >= 19) { stopRecording(); return 20; }
        return t + 1;
      }), 1000);
    } catch {
      // Microphone permission denied
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const uploadVoiceNote = async () => {
    if (!recordedBlob || !editingGuest) return;
    setIsUploadingVoice(true);
    try {
      const formData = new FormData();
      formData.append('file', recordedBlob, `voice_${Date.now()}.webm`);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/events/${eventId}/guests/${editingGuest.id}/voice-note`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.voice_note_url) {
        setGuestForm(prev => ({ ...prev, voice_note_url: res.data.voice_note_url }));
      }
    } catch {
      // upload failed
    } finally {
      setIsUploadingVoice(false);
    }
  };

  const togglePreview = () => {
    if (!audioPreviewRef.current) return;
    if (isPlaying) {
      audioPreviewRef.current.pause();
      setIsPlaying(false);
    } else {
      audioPreviewRef.current.play();
      setIsPlaying(true);
    }
  };

  const clearRecording = () => {
    setRecordedBlob(null);
    setGuestForm(prev => ({ ...prev, voice_note_url: '' }));
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const previewSrc = hasNewRecording ? URL.createObjectURL(recordedBlob) : (hasExistingVoice ? `${process.env.REACT_APP_BACKEND_URL}${guestForm.voice_note_url}` : null);

  // Idle: no recording, no existing voice note
  if (!hasExistingVoice && !hasNewRecording && !isRecording) {
    return (
      <div className="space-y-2">
        <p className="text-xs admin-text-secondary mb-2">Say it, in your own voice</p>
        {isTouchDevice ? (
          <button
            type="button"
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
            className="w-full py-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center gap-1.5 text-gray-500 hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)] transition-colors"
            data-testid="voice-hold-to-record"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
            <span className="text-xs font-medium">Hold to record</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            className="w-full py-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center gap-1.5 text-gray-500 hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)] transition-colors"
            data-testid="voice-click-to-record"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
            <span className="text-xs font-medium">Click to start recording</span>
          </button>
        )}
      </div>
    );
  }

  // Recording in progress
  if (isRecording) {
    return (
      <div className="space-y-2">
        <div className="w-full py-6 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex flex-col items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
          <span className="text-sm font-medium text-red-600 dark:text-red-400">Recording... {formatTime(recordingTime)}</span>
          <span className="text-xs text-red-400">max 0:20</span>
          <button
            type="button"
            onClick={stopRecording}
            className="mt-1 px-4 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
            data-testid="voice-stop-recording"
          >
            {isTouchDevice ? 'Release to finish' : 'Click to stop'}
          </button>
        </div>
      </div>
    );
  }

  // Has recording (new or existing) — preview/upload/re-record
  return (
    <div className="space-y-2">
      <div className="w-full p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">
          {hasNewRecording && !guestForm.voice_note_url ? 'Your message is ready' : 'Voice note saved'}
        </p>
        <audio ref={audioPreviewRef} src={previewSrc} onEnded={() => setIsPlaying(false)} />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={togglePreview}
            className="flex-1 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 text-xs font-medium text-green-700 dark:text-green-400 hover:bg-green-50 transition-colors"
            data-testid="voice-listen"
          >
            {isPlaying ? 'Pause' : 'Listen'}
          </button>
          <button
            type="button"
            onClick={clearRecording}
            className="py-1.5 px-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
            data-testid="voice-rerecord"
          >
            Re-record
          </button>
        </div>
        {hasNewRecording && !guestForm.voice_note_url && editingGuest && (
          <button
            type="button"
            onClick={uploadVoiceNote}
            disabled={isUploadingVoice}
            className="w-full mt-2 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
            data-testid="voice-upload"
          >
            {isUploadingVoice ? 'Saving...' : 'Save voice note'}
          </button>
        )}
      </div>
      <p className="text-xs admin-text-secondary">The guest will hear this when they find their table.</p>
    </div>
  );
}


// ─── Voice Recorder for Table Notes ──────────────────────────────
function TableVoiceRecorder({
  tableForm, setTableForm, editingTable, eventId,
  isRecording, setIsRecording, recordedBlob, setRecordedBlob,
  recordingTime, setRecordingTime, isUploadingVoice, setIsUploadingVoice,
  mediaRecorderRef, recordingTimerRef, chunksRef,
}) {
  const audioPreviewRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const hasExistingVoice = !!tableForm.table_voice_note_url;
  const hasNewRecording = !!recordedBlob;
  const isTouchDevice = 'ontouchstart' in window;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        setRecordedBlob(new Blob(chunksRef.current, { type: mr.mimeType }));
        clearInterval(recordingTimerRef.current);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => {
        if (t >= 19) { stopRecording(); return 20; }
        return t + 1;
      }), 1000);
    } catch { /* mic denied */ }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const uploadVoiceNote = async () => {
    if (!recordedBlob || !editingTable) return;
    setIsUploadingVoice(true);
    try {
      const formData = new FormData();
      formData.append('file', recordedBlob, `tablevoice_${Date.now()}.webm`);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/events/${eventId}/tables/${editingTable.id}/voice-note`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.voice_note_url) {
        setTableForm(prev => ({ ...prev, table_voice_note_url: res.data.voice_note_url }));
      }
    } catch { /* */ } finally { setIsUploadingVoice(false); }
  };

  const togglePreview = () => {
    if (!audioPreviewRef.current) return;
    if (isPlaying) { audioPreviewRef.current.pause(); setIsPlaying(false); }
    else { audioPreviewRef.current.play(); setIsPlaying(true); }
  };

  const clearRecording = () => {
    setRecordedBlob(null);
    setTableForm(prev => ({ ...prev, table_voice_note_url: '' }));
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const previewSrc = hasNewRecording ? URL.createObjectURL(recordedBlob) : (hasExistingVoice ? `${process.env.REACT_APP_BACKEND_URL}${tableForm.table_voice_note_url}` : null);

  if (!hasExistingVoice && !hasNewRecording && !isRecording) {
    return (
      <div className="space-y-2">
        <p className="text-xs admin-text-secondary mb-2">Say it, in your own voice</p>
        <button type="button" onClick={isTouchDevice ? undefined : startRecording} onTouchStart={isTouchDevice ? startRecording : undefined} onTouchEnd={isTouchDevice ? stopRecording : undefined}
          className="w-full py-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 flex flex-col items-center gap-1.5 text-gray-500 hover:border-[var(--admin-accent)] hover:text-[var(--admin-accent)] transition-colors"
          data-testid="table-voice-record">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
          <span className="text-xs font-medium">{isTouchDevice ? 'Hold to record' : 'Click to start recording'}</span>
        </button>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="w-full py-6 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex flex-col items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-medium text-red-600 dark:text-red-400">Recording... {formatTime(recordingTime)}</span>
        <button type="button" onClick={stopRecording} className="mt-1 px-4 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium" data-testid="table-voice-stop">
          {isTouchDevice ? 'Release to finish' : 'Click to stop'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="w-full p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">
          {hasNewRecording && !tableForm.table_voice_note_url ? 'Your message is ready' : 'Voice note saved'}
        </p>
        <audio ref={audioPreviewRef} src={previewSrc} onEnded={() => setIsPlaying(false)} />
        <div className="flex gap-2">
          <button type="button" onClick={togglePreview} className="flex-1 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 text-xs font-medium text-green-700 dark:text-green-400" data-testid="table-voice-listen">
            {isPlaying ? 'Pause' : 'Listen'}
          </button>
          <button type="button" onClick={clearRecording} className="py-1.5 px-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-xs font-medium text-gray-500" data-testid="table-voice-rerecord">
            Re-record
          </button>
        </div>
        {hasNewRecording && !tableForm.table_voice_note_url && editingTable && (
          <button type="button" onClick={uploadVoiceNote} disabled={isUploadingVoice}
            className="w-full mt-2 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium disabled:opacity-50" data-testid="table-voice-upload">
            {isUploadingVoice ? 'Saving...' : 'Save voice note'}
          </button>
        )}
      </div>
      <p className="text-xs admin-text-secondary">All guests at this table will hear this.</p>
    </div>
  );
}



export default function EventSetup() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { adminDarkMode, toggleAdminDarkMode, adminTheme: th } = useTheme();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const bgImageInputRef = useRef(null);
  
  // Check if user is admin (qrtest@test.com)
  const isAdmin = user?.is_admin || false;

  const [event, setEvent] = useState(null);
  const [activeTab, setActiveTab] = useState('guests');
  const [collectedAutoOpenAdd, setCollectedAutoOpenAdd] = useState(false);
  const [guests, setGuests] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableFilter, setTableFilter] = useState('all');
  const [rsvpPageInfo, setRsvpPageInfo] = useState(null); // {engine, v2_template_id, slug, is_published}

  // Dialogs
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showThemePreview, setShowThemePreview] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [previewTheme, setPreviewTheme] = useState(null);

  // Support Form
  const [supportForm, setSupportForm] = useState({ subject: '', message: '' });
  const [sendingSupport, setSendingSupport] = useState(false);

  // Tier Info
  const [tierInfo, setTierInfo] = useState(null);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [trialDays, setTrialDays] = useState(3);
  const [startingTrial, setStartingTrial] = useState(false);
  
  // Check if user has paid tier (not free)
  const isPaidTier = tierInfo?.current_tier && tierInfo.current_tier !== 'free';

  // Custom Theme State
  const [customColors, setCustomColors] = useState({ ...defaultCustomColors });
  const [useBackgroundImage, setUseBackgroundImage] = useState(false);
  const [uploadingBgImage, setUploadingBgImage] = useState(false);
  
  // Upgrade Popup State
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [pendingPremiumFeatures, setPendingPremiumFeatures] = useState([]);
  
  // Push Notifications State
  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(false);
  const [savingPushSettings, setSavingPushSettings] = useState(false);

  // Floor Plan State
  const [selectedFloorPlanTable, setSelectedFloorPlanTable] = useState(null);
  const [selectedVenueElement, setSelectedVenueElement] = useState(null);
  const [selectedCustomPiece, setSelectedCustomPiece] = useState(null); // For custom piece properties
  const [selectedTableIds, setSelectedTableIds] = useState([]); // For bulk selection
  const [selectedElementIds, setSelectedElementIds] = useState([]); // For bulk selection
  const [venueElements, setVenueElements] = useState([]);
  const [customPieces, setCustomPieces] = useState([]); // For modular table building
  const [textBoxes, setTextBoxes] = useState([]); // For floor plan text labels
  const [selectedTextBox, setSelectedTextBox] = useState(null); // Selected text box ID
  const [savingFloorPlan, setSavingFloorPlan] = useState(false);
  const [showFloorPlanPreview, setShowFloorPlanPreview] = useState(false);
  const [autoLayoutLoading, setAutoLayoutLoading] = useState(false);
  const [uploadingFloorPlanBg, setUploadingFloorPlanBg] = useState(false);
  const [uploadingFinalFloorPlan, setUploadingFinalFloorPlan] = useState(false);

  // Undo/Redo History for Canvas Elements
  const [undoHistory, setUndoHistory] = useState([]);
  const [redoHistory, setRedoHistory] = useState([]);
  const MAX_HISTORY = 30;

  // Forms
  const [guestForm, setGuestForm] = useState({ full_name: '', group_name: '', notes: '', table_id: '', guest_message: '', voice_note_url: '' });
  const [noteMode, setNoteMode] = useState('write'); // 'write' | 'say'
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const chunksRef = useRef([]);
  const [tableForm, setTableForm] = useState({ table_name: '', shape: 'round', capacity: '', table_message: '', table_voice_note_url: '' });
  const [tableNoteMode, setTableNoteMode] = useState('write');
  const [tableRecordedBlob, setTableRecordedBlob] = useState(null);
  const [tableIsRecording, setTableIsRecording] = useState(false);
  const [tableRecordingTime, setTableRecordingTime] = useState(0);
  const [tableIsUploadingVoice, setTableIsUploadingVoice] = useState(false);
  const tableMediaRecorderRef = useRef(null);
  const tableRecordingTimerRef = useRef(null);
  const tableChunksRef = useRef([]);

  useEffect(() => {
    fetchData();
  }, [eventId]);

  // Initialize custom colors when event loads
  useEffect(() => {
    if (event) {
      if (event.custom_colors) {
        setCustomColors({ ...defaultCustomColors, ...event.custom_colors });
      }
      if (event.background_image_url) {
        setUseBackgroundImage(true);
      }
    }
  }, [event]);

  const fetchData = async () => {
    try {
      // Add cache-busting headers to ensure fresh data
      const noCacheConfig = { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } };
      const [eventRes, guestsRes, tablesRes, venueElementsRes, customPiecesRes, textBoxesRes, tierInfoRes, pricingRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`, noCacheConfig),
        axios.get(`${API}/events/${eventId}/guests`, noCacheConfig),
        axios.get(`${API}/events/${eventId}/tables`, noCacheConfig),
        axios.get(`${API}/events/${eventId}/venue-elements`, noCacheConfig),
        axios.get(`${API}/events/${eventId}/custom-pieces`, noCacheConfig).catch(() => ({ data: [] })), // Graceful fallback
        axios.get(`${API}/events/${eventId}/text-boxes`, noCacheConfig).catch(() => ({ data: [] })), // Graceful fallback
        axios.get(`${API}/events/${eventId}/tier-info`, noCacheConfig),
        axios.get(`${API}/pricing/tiers`, noCacheConfig)
      ]);
      
      // Normalize color_theme - map 'default' to 'coastalSilk'
      const eventData = eventRes.data;
      if (!eventData.color_theme || eventData.color_theme === 'default') {
        eventData.color_theme = 'coastalSilk';
      }
      
      setEvent(eventData);
      setGuests(guestsRes.data);
      setTables(tablesRes.data);
      setVenueElements(venueElementsRes.data);
      setCustomPieces(customPiecesRes.data || []);
      setTextBoxes(textBoxesRes.data || []);
      setTierInfo(tierInfoRes.data);
      setPricingTiers(pricingRes.data.tiers || []);
      setTrialDays(pricingRes.data.trial_days || 3);
      setPushNotificationsEnabled(eventData.push_notifications_enabled || false);

      // Fetch RSVP page info for engine detection
      try {
        const rsvpRes = await axios.get(`${API}/events/${eventId}/rsvp-page`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setRsvpPageInfo(rsvpRes.data);
      } catch {
        setRsvpPageInfo(null); // No RSVP page yet
      }
    } catch (error) {
      toast.error('Failed to load event data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Event Update
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    
    // Check if free user is trying to save premium features
    if (!isPaidTier) {
      const premiumFeatures = [];
      
      // Check for custom event message
      if (event.event_message && event.event_message.trim() !== '') {
        premiumFeatures.push('Custom Event Message');
      }
      
      // Check for non-default theme
      if (event.color_theme && event.color_theme !== 'coastalSilk') {
        premiumFeatures.push('Custom Color Theme');
      }
      
      // Check for customization mode
      if (event.theme_mode === 'customization') {
        premiumFeatures.push('Custom Color Settings');
      }
      
      // If trying to save premium features, show upgrade popup
      if (premiumFeatures.length > 0) {
        setPendingPremiumFeatures(premiumFeatures);
        setShowUpgradePopup(true);
        return;
      }
    }
    
    try {
      const updateData = {
        name: event.name,
        date: event.date,
        location: event.location,
        language: event.language,
        color_theme: event.color_theme || 'coastalSilk',
        theme_mode: event.theme_mode || 'preset',
        overlay_intensity: event.overlay_intensity || 50,
        event_message: event.event_message || null
      };
      
      // Include custom colors if in customization mode
      if (event.theme_mode === 'customization') {
        updateData.custom_colors = customColors;
      }
      
      await axios.put(`${API}/events/${eventId}`, updateData);
      toast.success('Event updated!');
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleThemeChange = (themeId) => {
    setEvent({ ...event, color_theme: themeId });
    setPreviewTheme(themeId);
  };

  const handleThemeModeChange = (mode) => {
    setEvent({ ...event, theme_mode: mode });
    if (mode === 'customization' && !event.custom_colors) {
      // Initialize custom colors from current preset theme
      const currentTheme = getTheme(event.color_theme || 'coastalSilk');
      setCustomColors({
        background: currentTheme.colors.background,
        primary: currentTheme.colors.primary,
        accent: currentTheme.colors.accent,
        tableButton: currentTheme.colors.tableButton,
        tableButtonText: currentTheme.colors.tableButtonText,
        text: currentTheme.colors.text,
        textSecondary: currentTheme.colors.textSecondary
      });
    }
  };

  const handleCustomColorChange = (colorKey, value) => {
    setCustomColors(prev => ({ ...prev, [colorKey]: value }));
  };

  const resetAllCustomColors = () => {
    const currentTheme = getTheme(event?.color_theme || 'coastalSilk');
    setCustomColors({
      background: currentTheme.colors.background,
      primary: currentTheme.colors.primary,
      accent: currentTheme.colors.accent,
      tableButton: currentTheme.colors.tableButton,
      tableButtonText: currentTheme.colors.tableButtonText,
      text: currentTheme.colors.text,
      textSecondary: currentTheme.colors.textSecondary
    });
    toast.success('Colors reset to preset defaults');
  };

  const handleBackgroundImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use PNG, JPG, or WEBP');
      return;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size: 10MB');
      return;
    }
    
    setUploadingBgImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/events/${eventId}/upload-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setEvent(prev => ({ ...prev, background_image_url: response.data.background_image_url }));
      setUseBackgroundImage(true);
      toast.success('Background image uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingBgImage(false);
    }
  };

  const handleRemoveBackgroundImage = async () => {
    try {
      await axios.delete(`${API}/events/${eventId}/background`);
      setEvent(prev => ({ ...prev, background_image_url: null }));
      setUseBackgroundImage(false);
      toast.success('Background image removed');
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const openThemePreview = () => {
    setPreviewTheme(event?.color_theme || 'coastalSilk');
    setShowThemePreview(true);
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Guest Management
  const handleSaveGuest = async (e) => {
    e.preventDefault();
    try {
      const data = { ...guestForm, table_id: guestForm.table_id || null };
      if (editingGuest) {
        await axios.put(`${API}/events/${eventId}/guests/${editingGuest.id}`, data);
        toast.success('Guest updated!');
      } else {
        await axios.post(`${API}/events/${eventId}/guests`, data);
        toast.success('Guest added!');
      }
      setShowGuestDialog(false);
      setEditingGuest(null);
      setGuestForm({ full_name: '', group_name: '', notes: '', table_id: '', guest_message: '' });
      fetchData();
    } catch (error) {
      const errorCode = error.response?.data?.detail;
      if (errorCode === 'PLAN_LIMIT_GUESTS') {
        toast.error(t('planLimitGuests'));
      } else {
        toast.error('Failed to save guest');
      }
    }
  };

  const handleDeleteGuest = async (guestId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/guests/${guestId}`);
      toast.success('Guest deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete guest');
    }
  };

  const openEditGuest = (guest) => {
    setEditingGuest(guest);
    setGuestForm({
      full_name: guest.full_name,
      group_name: guest.group_name || '',
      notes: guest.notes || '',
      table_id: guest.table_id || '',
      guest_message: guest.guest_message || '',
      voice_note_url: guest.voice_note_url || ''
    });
    setNoteMode(guest.voice_note_url ? 'say' : 'write');
    setRecordedBlob(null);
    setShowGuestDialog(true);
  };

  // Quick Assign - Assign guest to table from floor plan
  const handleQuickAssignGuest = async (guestId, tableId, seatIndex) => {
    try {
      // seatIndex is 0-based, but seat_number should be 1-based for display
      const seatNumber = seatIndex !== undefined ? seatIndex + 1 : null;
      await axios.put(`${API}/events/${eventId}/guests/${guestId}`, { 
        table_id: tableId,
        seat_number: seatNumber
      });
      const guest = guests.find(g => g.id === guestId);
      const table = tables.find(t => t.id === tableId);
      toast.success(`${guest?.full_name || 'Guest'} assigned to ${table?.table_name || 'table'}${seatNumber ? ` (Seat ${seatNumber})` : ''}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to assign guest');
    }
  };

  // Remove guest from seat (unassign from table)
  const handleRemoveGuestFromSeat = async (guestId) => {
    try {
      await axios.put(`${API}/events/${eventId}/guests/${guestId}`, { table_id: null });
      const guest = guests.find(g => g.id === guestId);
      toast.success(`${guest?.full_name || 'Guest'} removed from seat`);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove guest from seat');
    }
  };

  // Swap two guests between their seats
  const handleSwapGuests = async (guest1Id, guest2Id) => {
    try {
      const guest1 = guests.find(g => g.id === guest1Id);
      const guest2 = guests.find(g => g.id === guest2Id);
      
      if (!guest1 || !guest2) {
        toast.error('Could not find guests to swap');
        return;
      }

      // Swap their table assignments
      await Promise.all([
        axios.put(`${API}/events/${eventId}/guests/${guest1Id}`, { table_id: guest2.table_id }),
        axios.put(`${API}/events/${eventId}/guests/${guest2Id}`, { table_id: guest1.table_id })
      ]);
      
      toast.success(`Swapped ${guest1.full_name} and ${guest2.full_name}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to swap guests');
    }
  };

  // Table Management
  const handleSaveTable = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        table_name: tableForm.table_name, 
        shape: tableForm.shape || 'round',
        capacity: tableForm.capacity ? parseInt(tableForm.capacity) : null,
        table_message: tableForm.table_message || null
      };
      if (editingTable) {
        await axios.put(`${API}/events/${eventId}/tables/${editingTable.id}`, data);
        toast.success('Table updated!');
      } else {
        await axios.post(`${API}/events/${eventId}/tables`, data);
        toast.success('Table added!');
      }
      setShowTableDialog(false);
      setEditingTable(null);
      setTableForm({ table_name: '', shape: 'round', capacity: '', table_message: '', table_voice_note_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save table');
    }
  };

  const handleDeleteTable = async (tableId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/tables/${tableId}`);
      toast.success('Table deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete table');
    }
  };

  const openEditTable = (table) => {
    setEditingTable(table);
    setTableForm({ 
      table_name: table.table_name, 
      shape: table.shape || 'round',
      capacity: table.capacity?.toString() || '',
      table_message: table.table_message || '',
      table_voice_note_url: table.table_voice_note_url || ''
    });
    setTableNoteMode(table.table_voice_note_url ? 'say' : 'write');
    setTableRecordedBlob(null);
    setShowTableDialog(true);
  };

  // Download Design HTML for motion graphics
  const handleDownloadDesignHTML = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/export-design-html`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${event?.name || 'event'}_design.html`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Design HTML downloaded!');
    } catch (error) {
      toast.error('Failed to download design HTML');
    }
  };

  // Download CSV Template with shape options
  const handleDownloadTemplate = () => {
    const csvContent = `# SEATING CHART CSV TEMPLATE
# 
# Available table_shape options:
#   - round (default, 8 seats)
#   - square (4 seats)
#   - rectangle (10 seats)
#   - oval (12 seats)
#   - lshape (8 seats)
#   - ushape (12 seats)
#   - sshape (10 seats)
#   - crescent (6 seats)
#
# The "seats" column is optional - uses shape defaults if not specified
# Lines starting with # are ignored
#
full_name,table_name,table_shape,seats,group_name,notes,personal_note
John Smith,Main Table,round,8,Family,Vegetarian,Welcome John!
Jane Doe,Main Table,round,8,Family,,
Bob Wilson,VIP Table,rectangle,12,VIP,Gluten-free,
Alice Brown,Kids Corner,square,4,Kids,,Have fun!
Tom Davis,Lounge,lshape,8,Friends,,
Sarah Miller,Bar Area,crescent,6,Colleagues,,`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'seating_chart_template.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // CSV Upload
  const [showGenerateFloorPlan, setShowGenerateFloorPlan] = useState(false);
  const [newTableCount, setNewTableCount] = useState(0);
  
  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/events/${eventId}/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { guests_created, guests_updated, tables_created, new_table_ids } = response.data;
      
      if (guests_created === 0 && guests_updated === 0) {
        toast.error('No guests found in CSV. Make sure your CSV has a column named "full_name" or "name".');
      } else {
        toast.success(`Import complete! ${guests_created} guests added, ${guests_updated} updated, ${tables_created} tables created.`);
        // Refresh the data
        await fetchData();
        
        // Show option to generate floor plan if new tables were created
        if (tables_created > 0) {
          setNewTableCount(tables_created);
          setShowGenerateFloorPlan(true);
        }
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      const errorCode = error.response?.data?.detail;
      if (errorCode === 'PLAN_LIMIT_GUESTS') {
        toast.error(t('planLimitGuests'));
      } else {
        toast.error(error.response?.data?.detail || 'Failed to import CSV. Please check the file format.');
      }
    }
    e.target.value = '';
  };

  // Unassign All Guests (clear all table assignments)
  const handleUnassignAllGuests = async () => {
    if (!window.confirm('Are you sure you want to unassign ALL guests from their tables? This will clear all seating assignments but keep the guests.')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/events/${eventId}/guests/unassign-all`);
      toast.success(`Unassigned ${response.data.updated_count} guests from their tables`);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign guests');
    }
  };

  // Debug: Check table-guest sync status
  const handleCheckSync = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/debug/table-guest-sync`);
      const data = response.data;
      
      console.log('[SYNC DEBUG]', data);
      
      if (data.issues && data.issues.length > 0) {
        toast.error(`Found ${data.issues.length} sync issues. Check console for details.`);
        data.issues.forEach(issue => console.warn('[SYNC ISSUE]', issue));
      } else if (data.orphaned_guests && data.orphaned_guests.length > 0) {
        toast.warning(`Found ${data.orphaned_guests.length} orphaned guests. Try "Sync Table IDs" to fix.`);
      } else {
        toast.success('All guests are properly synced with their tables!');
      }
    } catch (error) {
      toast.error('Failed to check sync status');
    }
  };

  // Sync orphaned guest table IDs
  const handleSyncTableIds = async () => {
    if (!window.confirm('This will clear table assignments for guests whose tables no longer exist. Continue?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/events/${eventId}/guests/sync-table-ids`);
      const { cleared_count } = response.data;
      
      if (cleared_count > 0) {
        toast.success(`Cleared ${cleared_count} orphaned table assignments. You may need to reassign these guests.`);
        await fetchData();
      } else {
        toast.success('No orphaned assignments found. Everything is in sync!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync table IDs');
    }
  };

  // Merge duplicate tables (same name, different IDs)
  const handleMergeDuplicateTables = async () => {
    if (!window.confirm('This will merge duplicate tables (same name) and reassign guests. Tables on the Floor Plan will be kept. Continue?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/events/${eventId}/tables/merge-duplicates`);
      const { merged_count, reassigned_guests } = response.data;
      
      if (merged_count > 0) {
        toast.success(`Merged ${merged_count} duplicate tables, reassigned ${reassigned_guests} guests.`);
        await fetchData();
      } else {
        toast.success('No duplicate tables found. Everything is unique!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge duplicate tables');
    }
  };

  // Delete All Guests
  const handleDeleteAllGuests = async () => {
    if (!window.confirm(t('confirmDeleteAllGuests'))) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/events/${eventId}/guests`);
      toast.success(t('allGuestsDeleted').replace('{count}', response.data.deleted_count));
      await fetchData();
    } catch (error) {
      toast.error(t('deleteError'));
    }
  };


  // Trial Handler
  const handleStartTrial = async (tierId) => {
    setStartingTrial(true);
    try {
      await axios.post(`${API}/events/${eventId}/start-trial`, {
        event_id: eventId,
        tier: tierId
      });
      toast.success(`${trialDays}-day trial started!`);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  // Support Contact Handler
  const handleSendSupport = async (e) => {
    e.preventDefault();
    if (!supportForm.subject.trim() || !supportForm.message.trim()) {
      toast.error(t('supportValidationError'));
      return;
    }
    
    setSendingSupport(true);
    try {
      await axios.post(`${API}/support/contact`, {
        subject: supportForm.subject,
        message: supportForm.message
      });
      toast.success(t('supportSuccess'));
      setSupportForm({ subject: '', message: '' });
      setShowSupportDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('supportError'));
    } finally {
      setSendingSupport(false);
    }
  };
  
  // Push Notifications Toggle Handler
  const handlePushToggle = async (enabled) => {
    setSavingPushSettings(true);
    try {
      await axios.put(`${API}/events/${eventId}/push-settings?enabled=${enabled}`);
      setPushNotificationsEnabled(enabled);
      toast.success(enabled ? 'Push notifications enabled' : 'Push notifications disabled');
    } catch (error) {
      toast.error('Failed to update push settings');
    } finally {
      setSavingPushSettings(false);
    }
  };

  // ========== UNDO/REDO SYSTEM ==========
  // Save state before a change (for undo)
  const saveToUndoHistory = useCallback((type, id, previousState) => {
    setUndoHistory(prev => {
      const newHistory = [...prev, { type, id, previousState, timestamp: Date.now() }];
      // Keep only last MAX_HISTORY items
      return newHistory.slice(-MAX_HISTORY);
    });
    // Clear redo history when new action is performed
    setRedoHistory([]);
  }, [MAX_HISTORY]);

  // Undo last action
  const handleUndo = useCallback(async () => {
    if (undoHistory.length === 0) {
      toast.info('Nothing to undo');
      return;
    }

    const lastAction = undoHistory[undoHistory.length - 1];
    const { type, id, previousState } = lastAction;

    // Save current state for redo
    let currentState;
    if (type === 'table') {
      currentState = tables.find(t => t.id === id);
    } else if (type === 'piece') {
      currentState = customPieces.find(p => p.id === id);
    } else if (type === 'element') {
      currentState = venueElements.find(e => e.id === id);
    }

    if (currentState) {
      setRedoHistory(prev => [...prev, { type, id, previousState: currentState }]);
    }

    // Restore previous state
    try {
      if (type === 'table') {
        setTables(prev => prev.map(t => t.id === id ? { ...t, ...previousState } : t));
        await axios.put(`${API}/events/${eventId}/tables/${id}`, previousState);
      } else if (type === 'piece') {
        setCustomPieces(prev => prev.map(p => p.id === id ? { ...p, ...previousState } : p));
        await axios.put(`${API}/events/${eventId}/custom-pieces/${id}`, previousState);
      } else if (type === 'element') {
        setVenueElements(prev => prev.map(e => e.id === id ? { ...e, ...previousState } : e));
        await axios.put(`${API}/events/${eventId}/venue-elements/${id}`, previousState);
      }
      
      // Remove from undo history
      setUndoHistory(prev => prev.slice(0, -1));
      toast.success('Undo successful');
    } catch (error) {
      toast.error('Failed to undo');
    }
  }, [undoHistory, tables, customPieces, venueElements, eventId]);

  // Redo last undone action
  const handleRedo = useCallback(async () => {
    if (redoHistory.length === 0) {
      toast.info('Nothing to redo');
      return;
    }

    const lastRedo = redoHistory[redoHistory.length - 1];
    const { type, id, previousState } = lastRedo;

    // Save current state for undo
    let currentState;
    if (type === 'table') {
      currentState = tables.find(t => t.id === id);
    } else if (type === 'piece') {
      currentState = customPieces.find(p => p.id === id);
    } else if (type === 'element') {
      currentState = venueElements.find(e => e.id === id);
    }

    if (currentState) {
      setUndoHistory(prev => [...prev, { type, id, previousState: currentState }]);
    }

    // Apply redo state
    try {
      if (type === 'table') {
        setTables(prev => prev.map(t => t.id === id ? { ...t, ...previousState } : t));
        await axios.put(`${API}/events/${eventId}/tables/${id}`, previousState);
      } else if (type === 'piece') {
        setCustomPieces(prev => prev.map(p => p.id === id ? { ...p, ...previousState } : p));
        await axios.put(`${API}/events/${eventId}/custom-pieces/${id}`, previousState);
      } else if (type === 'element') {
        setVenueElements(prev => prev.map(e => e.id === id ? { ...e, ...previousState } : e));
        await axios.put(`${API}/events/${eventId}/venue-elements/${id}`, previousState);
      }
      
      // Remove from redo history
      setRedoHistory(prev => prev.slice(0, -1));
      toast.success('Redo successful');
    } catch (error) {
      toast.error('Failed to redo');
    }
  }, [redoHistory, tables, customPieces, venueElements, eventId]);

  // Keyboard listener for Ctrl+Z and Ctrl+Shift+Z
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
      // Also support Ctrl+Y for redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Floor Plan Handlers
  const handleUpdateTablePosition = useCallback(async (tableId, updates) => {
    // Save current state for undo (only position, size, rotation)
    const currentTable = tables.find(t => t.id === tableId);
    if (currentTable) {
      const undoState = {
        position_x: currentTable.position_x,
        position_y: currentTable.position_y,
        width: currentTable.width,
        height: currentTable.height,
        rotation: currentTable.rotation,
      };
      saveToUndoHistory('table', tableId, undoState);
    }
    
    // Update local state IMMEDIATELY (optimistic update)
    setTables(prev => prev.map(t => 
      t.id === tableId ? { ...t, ...updates } : t
    ));
    
    // Then persist to backend
    try {
      await axios.put(`${API}/events/${eventId}/tables/${tableId}`, updates);
    } catch (error) {
      // Revert on error (optional: could refetch)
      toast.error('Failed to update table position');
    }
  }, [eventId, tables, saveToUndoHistory]);

  const handleDeleteFloorPlanTable = useCallback(async (tableId) => {
    // Delete the table entirely (from both floor plan and tables list)
    try {
      await axios.delete(`${API}/events/${eventId}/tables/${tableId}`);
      setTables(prev => prev.filter(t => t.id !== tableId));
      setSelectedFloorPlanTable(null);
      toast.success('Table deleted');
    } catch (error) {
      toast.error('Failed to delete table');
    }
  }, [eventId]);

  const handleAddFloorPlanTable = useCallback(async (tableData) => {
    try {
      const response = await axios.post(`${API}/events/${eventId}/floor-plan/add-table`, tableData);
      const returnedTable = response.data;
      
      // Check if this is an existing table that was updated (had no position before)
      // vs a completely new table that was created
      setTables(prev => {
        const existingIndex = prev.findIndex(t => t.id === returnedTable.id);
        if (existingIndex >= 0) {
          // Update existing table with new position
          const updated = [...prev];
          updated[existingIndex] = returnedTable;
          return updated;
        } else {
          // Add new table
          return [...prev, returnedTable];
        }
      });
      toast.success('Table added to floor plan');
    } catch (error) {
      toast.error('Failed to add table');
    }
  }, [eventId]);

  const handleSaveFloorPlan = useCallback(async () => {
    // Check if user has premium tier
    if (!isPaidTier) {
      toast.error(
        <div className="flex flex-col gap-1">
          <span className="font-medium">Premium Feature</span>
          <span className="text-sm">Upgrade to save your floor plan layout.</span>
        </div>,
        { duration: 4000 }
      );
      return;
    }
    
    setSavingFloorPlan(true);
    try {
      // Tables on the floor plan (have positions)
      const tablesWithPositions = tables
        .filter(t => t.position_x !== null && t.position_y !== null)
        .map(t => ({
          id: t.id,
          position_x: t.position_x,
          position_y: t.position_y,
          shape: t.shape,
          seats: t.seats,
          rotation: t.rotation
        }));
      
      // Tables NOT on the floor plan (need positions cleared)
      const tablesWithoutPositions = tables
        .filter(t => t.position_x === null || t.position_y === null)
        .map(t => t.id);
      
      await axios.put(`${API}/events/${eventId}/floor-plan`, { 
        tables: tablesWithPositions,
        removed_table_ids: tablesWithoutPositions
      });
      toast.success('Floor plan saved!');
    } catch (error) {
      toast.error('Failed to save floor plan');
    } finally {
      setSavingFloorPlan(false);
    }
  }, [eventId, tables, isPaidTier]);

  const handleAutoLayout = useCallback(async () => {
    if (tables.length === 0) {
      toast.error('No tables to arrange. Add some tables first.');
      return;
    }
    
    setAutoLayoutLoading(true);
    try {
      const response = await axios.post(`${API}/events/${eventId}/floor-plan/auto-layout`);
      setTables(response.data);
      toast.success(`Auto-arranged ${response.data.length} tables!`);
    } catch (error) {
      toast.error('Failed to auto-arrange tables');
    } finally {
      setAutoLayoutLoading(false);
    }
  }, [eventId, tables.length]);

  // Generate Floor Plan - places all unplaced tables and assigns guests to seats
  const handleGenerateFloorPlan = useCallback(async () => {
    setAutoLayoutLoading(true);
    try {
      const response = await axios.post(`${API}/events/${eventId}/floor-plan/generate`);
      
      if (response.data.tables_placed === 0) {
        toast.info('All tables are already on the floor plan');
      } else {
        toast.success(`Generated floor plan: ${response.data.tables_placed} tables placed, ${response.data.guests_seated} guests seated!`);
      }
      
      // Refresh tables and guests to get updated positions and seat numbers
      const [tablesRes, guestsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/tables`),
        axios.get(`${API}/events/${eventId}/guests`)
      ]);
      setTables(tablesRes.data);
      setGuests(guestsRes.data);
      
    } catch (error) {
      console.error('Generate floor plan error:', error);
      toast.error('Failed to generate floor plan');
    } finally {
      setAutoLayoutLoading(false);
    }
  }, [eventId]);

  // Venue Element Handlers
  const handleAddVenueElement = useCallback(async (elementData) => {
    try {
      const response = await axios.post(`${API}/events/${eventId}/venue-elements`, elementData);
      setVenueElements(prev => [...prev, response.data]);
      toast.success('Element added to floor plan');
    } catch (error) {
      toast.error('Failed to add element');
    }
  }, [eventId]);

  const handleUpdateVenueElement = useCallback(async (elementId, updates) => {
    // Save current state for undo (only position, size, rotation)
    const currentElement = venueElements.find(e => e.id === elementId);
    if (currentElement && (updates.position_x !== undefined || updates.position_y !== undefined || 
        updates.width !== undefined || updates.height !== undefined || updates.rotation !== undefined)) {
      const undoState = {
        position_x: currentElement.position_x,
        position_y: currentElement.position_y,
        width: currentElement.width,
        height: currentElement.height,
        rotation: currentElement.rotation,
      };
      saveToUndoHistory('element', elementId, undoState);
    }
    
    // Optimistic update
    setVenueElements(prev => prev.map(e => 
      e.id === elementId ? { ...e, ...updates } : e
    ));
    
    try {
      await axios.put(`${API}/events/${eventId}/venue-elements/${elementId}`, updates);
    } catch (error) {
      toast.error('Failed to update element');
    }
  }, [eventId, venueElements, saveToUndoHistory]);

  const handleDeleteVenueElement = useCallback(async (elementId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/venue-elements/${elementId}`);
      setVenueElements(prev => prev.filter(e => e.id !== elementId));
      setSelectedVenueElement(null);
      toast.success('Element removed');
    } catch (error) {
      toast.error('Failed to remove element');
    }
  }, [eventId]);

  // Duplicate Handlers
  const handleDuplicateTable = useCallback(async (table) => {
    try {
      // Deep copy all properties including seat_offsets and label offsets
      const newTable = {
        name: `${table.name || table.table_name || 'Table'} (Copy)`,
        shape: table.shape,
        seats: table.seats,
        position_x: (table.position_x || 0) + 30,
        position_y: (table.position_y || 0) + 30,
        rotation: table.rotation || 0,
        width: table.width,
        height: table.height,
        table_message: table.table_message || null,
        // Include seat_offsets to preserve moved seat positions
        seat_offsets: table.seat_offsets ? JSON.parse(JSON.stringify(table.seat_offsets)) : null,
        // Include label offsets if the label was moved
        label_offset_x: table.label_offset_x || null,
        label_offset_y: table.label_offset_y || null,
      };
      
      const response = await axios.post(`${API}/events/${eventId}/floor-plan/add-table`, newTable);
      setTables(prev => [...prev, response.data]);
      toast.success('Table duplicated!');
    } catch (error) {
      toast.error('Failed to duplicate table');
    }
  }, [eventId]);

  const handleDuplicateVenueElement = useCallback(async (element) => {
    try {
      const newElement = {
        type: element.type,
        label: `${element.label || element.type} (Copy)`,
        position_x: (element.position_x || 0) + 30,
        position_y: (element.position_y || 0) + 30,
        width: element.width,
        height: element.height,
        rotation: element.rotation || 0,
      };
      
      const response = await axios.post(`${API}/events/${eventId}/venue-elements`, newElement);
      setVenueElements(prev => [...prev, response.data]);
      toast.success('Element duplicated!');
    } catch (error) {
      toast.error('Failed to duplicate element');
    }
  }, [eventId]);

  const handleDuplicateCustomPiece = useCallback(async (piece) => {
    try {
      // Deep copy all properties including seat_offsets for moved seats
      const newPiece = {
        piece_type: piece.piece_type,
        label: `${piece.label || 'Piece'} (Copy)`,
        position_x: (piece.position_x || 0) + 30,
        position_y: (piece.position_y || 0) + 30,
        width: piece.width,
        height: piece.height,
        rotation: piece.rotation || 0,
        seats: piece.seats || 4,
        // Include seat_offsets to preserve moved seat positions
        seat_offsets: piece.seat_offsets ? JSON.parse(JSON.stringify(piece.seat_offsets)) : null,
      };
      
      const response = await axios.post(`${API}/events/${eventId}/custom-pieces`, newPiece);
      setCustomPieces(prev => [...prev, response.data]);
      toast.success('Piece duplicated!');
    } catch (error) {
      console.error('Duplicate piece error:', error.response?.data || error);
      toast.error('Failed to duplicate piece');
    }
  }, [eventId]);

  // Floor Plan Background Handlers
  const handleUploadFloorPlanBackground = useCallback(async (file) => {
    setUploadingFloorPlanBg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/events/${eventId}/floor-plan/upload-background`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setEvent(prev => ({
        ...prev,
        floor_plan_background_url: response.data.floor_plan_background_url
      }));
      toast.success('Venue image uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload venue image');
    } finally {
      setUploadingFloorPlanBg(false);
    }
  }, [eventId]);

  const handleRemoveFloorPlanBackground = useCallback(async () => {
    try {
      await axios.delete(`${API}/events/${eventId}/floor-plan/background`);
      setEvent(prev => ({
        ...prev,
        floor_plan_background_url: null
      }));
      toast.success('Venue image removed');
    } catch (error) {
      toast.error('Failed to remove venue image');
    }
  }, [eventId]);

  const handleFloorPlanOpacityChange = useCallback(async (opacity) => {
    try {
      await axios.put(`${API}/events/${eventId}/floor-plan/background-opacity?opacity=${opacity}`);
      setEvent(prev => ({
        ...prev,
        floor_plan_background_opacity: opacity
      }));
    } catch (error) {
      console.error('Failed to update opacity:', error);
    }
  }, [eventId]);

  // Final Floor Plan Handlers (for guest public page)
  const handleUploadFinalFloorPlan = useCallback(async (file) => {
    setUploadingFinalFloorPlan(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/events/${eventId}/floor-plan/upload-final`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setEvent(prev => ({
        ...prev,
        final_floor_plan_url: response.data.final_floor_plan_url
      }));
      toast.success('Final floor plan uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload final floor plan');
    } finally {
      setUploadingFinalFloorPlan(false);
    }
  }, [eventId]);

  const handleRemoveFinalFloorPlan = useCallback(async () => {
    try {
      await axios.delete(`${API}/events/${eventId}/floor-plan/final`);
      setEvent(prev => ({
        ...prev,
        final_floor_plan_url: null
      }));
      toast.success('Final floor plan removed');
    } catch (error) {
      toast.error('Failed to remove final floor plan');
    }
  }, [eventId]);

  // Floor Plan Style Handler
  const handleFloorPlanStyleChange = useCallback(async (newStyle) => {
    try {
      const styleWithFlag = { ...newStyle, is_custom: true };
      await axios.put(`${API}/events/${eventId}`, { floor_plan_style: styleWithFlag });
      setEvent(prev => ({
        ...prev,
        floor_plan_style: styleWithFlag
      }));
    } catch (error) {
      console.error('Failed to update floor plan style:', error);
      toast.error('Failed to save style changes');
    }
  }, [eventId]);

  // Text Box Handlers
  const handleAddTextBox = useCallback(async (textBoxData) => {
    try {
      const response = await axios.post(`${API}/events/${eventId}/text-boxes`, textBoxData);
      setTextBoxes(prev => [...prev, response.data]);
      setSelectedTextBox(response.data.id);
      toast.success('Text box added! Double-click to edit text.');
    } catch (error) {
      toast.error('Failed to add text box');
    }
  }, [eventId]);

  const handleUpdateTextBox = useCallback(async (textBoxId, updates) => {
    try {
      // Optimistic update
      setTextBoxes(prev => prev.map(tb => tb.id === textBoxId ? { ...tb, ...updates } : tb));
      await axios.put(`${API}/events/${eventId}/text-boxes/${textBoxId}`, updates);
    } catch (error) {
      toast.error('Failed to update text box');
      // Revert on error
      const response = await axios.get(`${API}/events/${eventId}/text-boxes`);
      setTextBoxes(response.data || []);
    }
  }, [eventId]);

  const handleDeleteTextBox = useCallback(async (textBoxId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/text-boxes/${textBoxId}`);
      setTextBoxes(prev => prev.filter(tb => tb.id !== textBoxId));
      setSelectedTextBox(null);
      toast.success('Text box deleted');
    } catch (error) {
      toast.error('Failed to delete text box');
    }
  }, [eventId]);

  // Custom Pieces Handlers (for modular table building)
  const handleAddCustomPiece = useCallback(async (pieceData) => {
    try {
      const response = await axios.post(`${API}/events/${eventId}/custom-pieces`, pieceData);
      setCustomPieces(prev => [...prev, response.data]);
      toast.success('Piece added to floor plan');
    } catch (error) {
      toast.error('Failed to add piece');
    }
  }, [eventId]);

  const handleUpdateCustomPiece = useCallback(async (pieceId, updates) => {
    // Save current state for undo (only position, size, rotation)
    const currentPiece = customPieces.find(p => p.id === pieceId);
    if (currentPiece && (updates.position_x !== undefined || updates.position_y !== undefined || 
        updates.width !== undefined || updates.height !== undefined || updates.rotation !== undefined)) {
      const undoState = {
        position_x: currentPiece.position_x,
        position_y: currentPiece.position_y,
        width: currentPiece.width,
        height: currentPiece.height,
        rotation: currentPiece.rotation,
      };
      saveToUndoHistory('piece', pieceId, undoState);
    }
    
    // Optimistic update
    setCustomPieces(prev => prev.map(p => 
      p.id === pieceId ? { ...p, ...updates } : p
    ));
    
    try {
      await axios.put(`${API}/events/${eventId}/custom-pieces/${pieceId}`, updates);
    } catch (error) {
      toast.error('Failed to update piece');
    }
  }, [eventId, customPieces, saveToUndoHistory]);

  const handleDeleteCustomPiece = useCallback(async (pieceId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/custom-pieces/${pieceId}`);
      setCustomPieces(prev => prev.filter(p => p.id !== pieceId));
    } catch (error) {
      toast.error('Failed to remove piece');
    }
  }, [eventId]);

  const handleGroupPieces = useCallback(async (piecesToGroup) => {
    try {
      // Calculate bounding box of all pieces
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      let totalSeats = 0;
      
      piecesToGroup.forEach(piece => {
        minX = Math.min(minX, piece.position_x);
        minY = Math.min(minY, piece.position_y);
        maxX = Math.max(maxX, piece.position_x + (piece.width || 60));
        maxY = Math.max(maxY, piece.position_y + (piece.height || 60));
        totalSeats += piece.seats || 4;
      });
      
      // Generate a group ID
      const groupId = `group_${Date.now()}`;
      
      // Update all pieces to be part of this group (don't delete them)
      const response = await axios.post(`${API}/events/${eventId}/custom-pieces/group`, {
        piece_ids: piecesToGroup.map(p => p.id),
        group_id: groupId,
        table_data: {
          position_x: minX,
          position_y: minY,
          width: maxX - minX,
          height: maxY - minY,
          seats: totalSeats,
          table_name: `Custom Table ${tables.length + 1}`,
        }
      });
      
      // Update the pieces in state to have the group_id
      const groupedIds = piecesToGroup.map(p => p.id);
      setCustomPieces(prev => prev.map(p => 
        groupedIds.includes(p.id) ? { ...p, group_id: groupId } : p
      ));
      
      // Add the new table (it's a reference to the grouped pieces)
      setTables(prev => [...prev, response.data]);
      toast.success('Pieces grouped into table!');
    } catch (error) {
      toast.error('Failed to group pieces');
    }
  }, [eventId, tables.length]);

  // Handler to move a grouped table (moves all pieces in the group together)
  const handleMoveGroupedTable = useCallback(async (groupedTableId, deltaX, deltaY) => {
    // Find the grouped table
    const groupedTable = tables.find(t => t.id === groupedTableId && t.shape === 'grouped');
    if (!groupedTable || !groupedTable.grouped_piece_ids) return;
    
    // Get all pieces in this group
    const piecesInGroup = customPieces.filter(p => 
      groupedTable.grouped_piece_ids.includes(p.id)
    );
    
    // Update all pieces' positions locally (optimistic update)
    setCustomPieces(prev => prev.map(p => {
      if (groupedTable.grouped_piece_ids.includes(p.id)) {
        return {
          ...p,
          position_x: p.position_x + deltaX,
          position_y: p.position_y + deltaY
        };
      }
      return p;
    }));
    
    // Update grouped table position too
    setTables(prev => prev.map(t => {
      if (t.id === groupedTableId) {
        return {
          ...t,
          position_x: (t.position_x || 0) + deltaX,
          position_y: (t.position_y || 0) + deltaY
        };
      }
      return t;
    }));
    
    // Persist each piece to backend
    try {
      await Promise.all(piecesInGroup.map(p => 
        axios.put(`${API}/events/${eventId}/custom-pieces/${p.id}`, {
          position_x: p.position_x + deltaX,
          position_y: p.position_y + deltaY
        })
      ));
      
      // Also update the grouped table position
      await axios.put(`${API}/events/${eventId}/tables/${groupedTableId}`, {
        position_x: (groupedTable.position_x || 0) + deltaX,
        position_y: (groupedTable.position_y || 0) + deltaY
      });
    } catch (error) {
      toast.error('Failed to move grouped table');
    }
  }, [tables, customPieces, eventId]);

  // Filtered guests
  const filteredGuests = guests.filter(g => {
    const matchesSearch = g.full_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTable = tableFilter === 'all' || g.table_id === tableFilter || (tableFilter === 'unassigned' && !g.table_id);
    return matchesSearch && matchesTable;
  });

  if (loading) {
    return (
      <div className="min-h-screen admin-bg flex items-center justify-center transition-colors duration-300">
        <div className="animate-pulse admin-accent">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen admin-bg p-4 md:p-8 transition-colors duration-300">
      {/* Header */}
      <header className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="admin-text-secondary"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('back')}
          </Button>
          {/* Theme Toggle */}
          <button
            onClick={toggleAdminDarkMode}
            className="p-2 rounded-lg transition-all duration-200 admin-card admin-card-hover admin-icon"
            title={adminDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            data-testid="theme-toggle-btn"
          >
            {adminDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold admin-accent">
          {event?.name}
        </h1>
      </header>

      <main className="max-w-7xl mx-auto">
        {isAdmin && (
          <RescueBanner
            eventId={eventId}
            token={localStorage.getItem('token')}
            eventDate={event?.date}
            onJumpToCollected={() => setActiveTab('collected')}
          />
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="admin-card rounded-lg">
            <TabsTrigger value="guests">
              <Users className="h-4 w-4 mr-2" />
              {t('guestList')}
            </TabsTrigger>
            <TabsTrigger value="tables">
              <LayoutGrid className="h-4 w-4 mr-2" />
              {t('tables')}
            </TabsTrigger>
            <TabsTrigger value="floorplan">
              <Map className="h-4 w-4 mr-2" />
              Floor Plan
            </TabsTrigger>
            <TabsTrigger value="wishes">
              <Heart className="h-4 w-4 mr-2" />
              Love Gallery
            </TabsTrigger>
            <TabsTrigger value="moments">
              <Image className="h-4 w-4 mr-2" />
              Moments
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="collected" data-testid="tab-collected">
                <Users className="h-4 w-4 mr-2" />
                Collected
                <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-amber-500/20 text-amber-400 rounded">BETA</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="reveal">
              <Sun className="h-4 w-4 mr-2" />
              Reveal
              <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-amber-500/20 text-amber-400 rounded">BETA</span>
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="rsvp">
                <Mail className="h-4 w-4 mr-2" />
                Digital Invitation
                <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-amber-500/20 text-amber-400 rounded">BETA</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="rsvp-dashboard" data-testid="tab-rsvp-dashboard">
              <BarChart3 className="h-4 w-4 mr-2" />
              RSVP Tracker
            </TabsTrigger>
            <TabsTrigger value="settings">
              {t('eventDetails')}
            </TabsTrigger>
            <TabsTrigger value="upgrade">
              <Crown className="h-4 w-4 mr-2" />
              {t('upgrade')}
            </TabsTrigger>
            <TabsTrigger value="support">
              <SupportIcon className="h-4 w-4 mr-2" color="currentColor" />
              {t('supportContact')}
            </TabsTrigger>
          </TabsList>

          {/* Guests Tab */}
          <TabsContent value="guests" className="space-y-4">
            {/* Guest Summary Info */}
            {guests.length > 0 && (
              <div 
                className={`flex flex-wrap gap-3 text-xs px-4 py-2 rounded-lg border ${adminDarkMode ? 'bg-slate-900/30 border-slate-800 text-slate-400' : 'bg-slate-100 border-slate-200'}`}
                style={th.useInlineStyles ? { backgroundColor: th.colors.card === '#ffffff' ? '#f8fafc' : th.colors.card, borderColor: th.colors.border, color: th.colors.textSecondary } : {}}
              >
                <span>Total: <span 
                  className="font-medium"
                  style={th.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                >{guests.length}</span></span>
                <span>•</span>
                <span>Assigned: <span className="text-green-500 font-medium">{guests.filter(g => g.table_id).length}</span></span>
                <span>•</span>
                <span>Unassigned: <span className="text-amber-500 font-medium">{guests.filter(g => !g.table_id).length}</span></span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    placeholder={t('searchGuests')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-10 w-full sm:w-64 ${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200'}`}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    data-testid="guest-search-input"
                  />
                </div>
                <Select value={tableFilter} onValueChange={setTableFilter}>
                  <SelectTrigger 
                    className={`w-full sm:w-48 ${adminDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-slate-200'}`}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    data-testid="table-filter-select"
                  >
                    <SelectValue placeholder={t('filterByTable')} />
                  </SelectTrigger>
                  <SelectContent 
                    className={adminDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card } : {}}
                  >
                    <SelectItem value="all">{t('allTables')}</SelectItem>
                    <SelectItem value="unassigned">{t('noTableAssigned')}</SelectItem>
                    {tables.map(table => (
                      <SelectItem key={table.id} value={table.id}>{table.table_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 flex-wrap">
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleCSVUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}
                  style={th.useInlineStyles ? { borderColor: th.colors.border, color: th.colors.textSecondary } : {}}
                  data-testid="download-template-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('downloadTemplate') || 'Template'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}
                  style={th.useInlineStyles ? { borderColor: th.colors.border, color: th.colors.textSecondary } : {}}
                  data-testid="upload-csv-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t('uploadCSV')}
                </Button>
                {guests.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleUnassignAllGuests}
                      className="border-amber-900/50 text-amber-400 hover:bg-amber-900/20 hover:border-amber-500/50"
                      data-testid="unassign-all-guests-btn"
                    >
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Unassign All
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleSyncTableIds}
                      className="border-blue-900/50 text-blue-400 hover:bg-blue-900/20 hover:border-blue-500/50"
                      title="Fix orphaned table assignments where tables were deleted/recreated"
                      data-testid="sync-table-ids-btn"
                    >
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Sync Tables
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDeleteAllGuests}
                      className="border-red-900/50 text-red-400 hover:bg-red-900/20 hover:border-red-500/50"
                      data-testid="delete-all-guests-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t('deleteAllGuests')}
                    </Button>
                  </>
                )}
                <Dialog open={showGuestDialog} onOpenChange={(open) => { setShowGuestDialog(open); if (!open) { setEditingGuest(null); setGuestForm({ full_name: '', group_name: '', notes: '', table_id: '', guest_message: '', voice_note_url: '' }); setNoteMode('write'); setRecordedBlob(null); } }}>
                  <DialogTrigger asChild>
                    <Button className="admin-accent-bg" data-testid="add-guest-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('addGuest')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="admin-card admin-text max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-heading text-xl admin-accent">
                        {editingGuest ? t('edit') : t('addGuest')}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveGuest} className="space-y-4">
                      <div className="space-y-2">
                        <Label className="admin-text-secondary">{t('fullName')}</Label>
                        <Input
                          value={guestForm.full_name}
                          onChange={(e) => setGuestForm({ ...guestForm, full_name: e.target.value })}
                          className="admin-input"
                          required
                          data-testid="guest-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="admin-text-secondary">{t('group')}</Label>
                        <Input
                          value={guestForm.group_name}
                          onChange={(e) => setGuestForm({ ...guestForm, group_name: e.target.value })}
                          className="admin-input"
                          placeholder="Bride's family"
                          data-testid="guest-group-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="admin-text-secondary">{t('table')}</Label>
                        <Select value={guestForm.table_id || 'none'} onValueChange={(val) => setGuestForm({ ...guestForm, table_id: val === 'none' ? '' : val })}>
                          <SelectTrigger className="admin-input" data-testid="guest-table-select">
                            <SelectValue placeholder={t('noTableAssigned')} />
                          </SelectTrigger>
                          <SelectContent className="admin-card">
                            <SelectItem value="none">{t('noTableAssigned')}</SelectItem>
                            {tables.map(table => (
                              <SelectItem key={table.id} value={table.id}>{table.table_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="admin-text-secondary">{t('notes')}</Label>
                        <Input
                          value={guestForm.notes}
                          onChange={(e) => setGuestForm({ ...guestForm, notes: e.target.value })}
                          className="admin-input"
                          placeholder="Vegetarian, wheelchair access"
                          data-testid="guest-notes-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="admin-text-secondary">Private note</Label>
                          <p className="text-xs admin-text-secondary opacity-70 mt-0.5">
                            Shown only to this guest after they find their place.
                          </p>
                        </div>
                        {/* Write / Record voice toggle */}
                        <div className="flex gap-2 mb-2">
                          <button
                            type="button"
                            onClick={() => setNoteMode('write')}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              noteMode === 'write'
                                ? 'bg-[var(--admin-accent)] text-white'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                            }`}
                            data-testid="note-mode-write"
                          >
                            Write
                          </button>
                          <button
                            type="button"
                            onClick={() => setNoteMode('say')}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              noteMode === 'say'
                                ? 'bg-[var(--admin-accent)] text-white'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                            }`}
                            data-testid="note-mode-say"
                          >
                            Record voice
                          </button>
                        </div>

                        {noteMode === 'write' ? (
                          <>
                            <textarea
                              value={guestForm.guest_message}
                              onChange={(e) => setGuestForm({ ...guestForm, guest_message: e.target.value })}
                              className="w-full min-h-[60px] px-3 py-2 admin-input rounded-md placeholder:opacity-50 focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)]/50 text-sm"
                              placeholder="Write something personal for this guest…"
                              data-testid="guest-message-input"
                            />
                            <p className="text-xs admin-text-secondary">Only this guest will see it.</p>
                          </>
                        ) : (
                          <VoiceRecorder
                            guestForm={guestForm}
                            setGuestForm={setGuestForm}
                            editingGuest={editingGuest}
                            eventId={eventId}
                            isRecording={isRecording}
                            setIsRecording={setIsRecording}
                            recordedBlob={recordedBlob}
                            setRecordedBlob={setRecordedBlob}
                            recordingTime={recordingTime}
                            setRecordingTime={setRecordingTime}
                            isUploadingVoice={isUploadingVoice}
                            setIsUploadingVoice={setIsUploadingVoice}
                            mediaRecorderRef={mediaRecorderRef}
                            recordingTimerRef={recordingTimerRef}
                            chunksRef={chunksRef}
                          />
                        )}
                      </div>
                      <Button type="submit" className="w-full admin-accent-bg" data-testid="save-guest-btn">
                        <Save className="h-4 w-4 mr-2" />
                        {t('save')}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <p className="text-sm text-slate-500">{t('csvFormat')}</p>
            <p className="text-xs text-slate-600">{t('csvUploadHint')}</p>

            {/* Guests Table */}
            <div 
              className={`overflow-hidden rounded-xl border ${adminDarkMode ? 'glass-card-dark' : 'bg-white border-slate-200'}`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <Table>
                <TableHeader>
                  <TableRow 
                    className={`${adminDarkMode ? 'border-slate-800' : 'border-slate-200'} hover:bg-transparent`}
                    style={th.useInlineStyles ? { borderColor: th.colors.border } : {}}
                  >
                    <TableHead 
                      className="font-heading"
                      style={th.useInlineStyles ? { color: th.colors.accent } : { color: adminDarkMode ? 'rgb(245 158 11 / 0.8)' : th.colors?.accent }}
                    >
                      {t('fullName')}
                    </TableHead>
                    <TableHead 
                      className="font-heading"
                      style={th.useInlineStyles ? { color: th.colors.accent } : { color: adminDarkMode ? 'rgb(245 158 11 / 0.8)' : th.colors?.accent }}
                    >
                      {t('table')}
                    </TableHead>
                    <TableHead 
                      className="font-heading hidden md:table-cell"
                      style={th.useInlineStyles ? { color: th.colors.accent } : { color: adminDarkMode ? 'rgb(245 158 11 / 0.8)' : th.colors?.accent }}
                    >
                      {t('group')}
                    </TableHead>
                    <TableHead 
                      className="font-heading hidden lg:table-cell"
                      style={th.useInlineStyles ? { color: th.colors.accent } : { color: adminDarkMode ? 'rgb(245 158 11 / 0.8)' : th.colors?.accent }}
                    >
                      {t('notes')}
                    </TableHead>
                    <TableHead 
                      className="font-heading text-right"
                      style={th.useInlineStyles ? { color: th.colors.accent } : { color: adminDarkMode ? 'rgb(245 158 11 / 0.8)' : th.colors?.accent }}
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGuests.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={5} 
                        className="text-center py-8"
                        style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: '#64748b' }}
                      >
                        No guests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGuests.map((guest) => (
                      <TableRow 
                        key={guest.id} 
                        className={`${adminDarkMode ? 'border-slate-800' : 'border-slate-200'}`}
                        style={th.useInlineStyles ? { borderColor: th.colors.border } : {}}
                        data-testid={`guest-row-${guest.id}`}
                      >
                        <TableCell 
                          className="font-medium"
                          style={th.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                        >
                          {guest.full_name}
                        </TableCell>
                        <TableCell>
                          <span 
                            className="font-mono"
                            style={th.useInlineStyles ? { color: th.colors.accent } : { color: adminDarkMode ? '#f59e0b' : th.colors?.accent }}
                          >
                            {guest.table_name || '-'}
                          </span>
                        </TableCell>
                        <TableCell 
                          className="hidden md:table-cell"
                          style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          {guest.group_name || '-'}
                        </TableCell>
                        <TableCell 
                          className="hidden lg:table-cell"
                          style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                        >
                          {guest.notes || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditGuest(guest)}
                              className={adminDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}
                              data-testid={`edit-guest-${guest.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteGuest(guest.id)}
                              className={adminDarkMode ? 'text-slate-400 hover:text-red-500' : 'text-slate-500 hover:text-red-500'}
                              data-testid={`delete-guest-${guest.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {/* Generate Floor Plan Dialog - appears after CSV import with new tables */}
            <Dialog open={showGenerateFloorPlan} onOpenChange={setShowGenerateFloorPlan}>
              <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-[#D4AF37] flex items-center gap-2">
                    <Map className="h-5 w-5" />
                    Generate Floor Plan?
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-slate-300">
                    {newTableCount} new table{newTableCount !== 1 ? 's were' : ' was'} created from your CSV import.
                  </p>
                  <p className="text-slate-400 text-sm">
                    Would you like to automatically place them on the floor plan? Guests will be assigned to seats in CSV order. You can adjust positions afterwards.
                  </p>
                  <div className="flex gap-3 justify-end pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowGenerateFloorPlan(false)}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    >
                      Skip
                    </Button>
                    <Button
                      onClick={async () => {
                        setShowGenerateFloorPlan(false);
                        // Switch to Floor Plan tab and generate layout
                        setActiveTab('floorplan');
                        // Small delay to let tab switch, then generate floor plan
                        setTimeout(() => {
                          handleGenerateFloorPlan();
                        }, 500);
                      }}
                      className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Layout
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Tables Tab */}
          <TabsContent value="tables" className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {tables.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleMergeDuplicateTables}
                    className="border-purple-900/50 text-purple-400 hover:bg-purple-900/20 hover:border-purple-500/50"
                    title="Merge tables with the same name and reassign guests"
                    data-testid="merge-tables-btn"
                  >
                    <RefreshCcw className="h-4 w-4 mr-2" />
                    Merge Duplicates
                  </Button>
                )}
              </div>
              <Dialog open={showTableDialog} onOpenChange={(open) => { setShowTableDialog(open); if (!open) { setEditingTable(null); setTableForm({ table_name: '', shape: 'round', capacity: '', table_message: '', table_voice_note_url: '' }); setTableNoteMode('write'); setTableRecordedBlob(null); } }}>
                <DialogTrigger asChild>
                  <Button className="admin-accent-bg" data-testid="add-table-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('addTable')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="admin-card admin-text">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl admin-accent">
                      {editingTable ? t('edit') : t('addTable')}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveTable} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="admin-text-secondary">{t('tableName')}</Label>
                      <Input
                        value={tableForm.table_name}
                        onChange={(e) => setTableForm({ ...tableForm, table_name: e.target.value })}
                        className="admin-input"
                        placeholder="Table 1, VIP"
                        required
                        data-testid="table-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="admin-text-secondary">{t('tableShape') || 'Table Shape'}</Label>
                      <Select 
                        value={tableForm.shape} 
                        onValueChange={(value) => setTableForm({ ...tableForm, shape: value })}
                      >
                        <SelectTrigger className="admin-input" data-testid="table-shape-select">
                          <SelectValue placeholder="Select shape" />
                        </SelectTrigger>
                        <SelectContent className="admin-card max-h-[300px]">
                          <SelectItem value="round" className="admin-text">
                            <span className="flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-current rounded-full inline-block" />
                              Round Table
                            </span>
                          </SelectItem>
                          <SelectItem value="square" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="2" y="2" width="12" height="12" rx="2" />
                              </svg>
                              Square Table
                            </span>
                          </SelectItem>
                          <SelectItem value="rectangle" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-4" viewBox="0 0 20 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="2" y="5" width="16" height="6" rx="1" />
                              </svg>
                              Long Table
                            </span>
                          </SelectItem>
                          <SelectItem value="oval" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-4" viewBox="0 0 20 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <ellipse cx="10" cy="8" rx="8" ry="5" />
                              </svg>
                              Oval Table
                            </span>
                          </SelectItem>
                          <SelectItem value="lshape" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M2 2 L6 2 L6 10 L14 10 L14 14 L2 14 Z" />
                              </svg>
                              L-Shape Table
                            </span>
                          </SelectItem>
                          <SelectItem value="ushape" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M2 2 L4 2 L4 10 L12 10 L12 2 L14 2 L14 14 L2 14 Z" />
                              </svg>
                              U-Shape Table
                            </span>
                          </SelectItem>
                          <SelectItem value="halfround" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M2 12 Q8 4 14 12" />
                              </svg>
                              Half Round
                            </span>
                          </SelectItem>
                          <SelectItem value="sshape" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M2 6 Q5 2 8 6 Q11 10 14 6" />
                              </svg>
                              Serpentine 2
                            </span>
                          </SelectItem>
                          <SelectItem value="serpentine3" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-4" viewBox="0 0 20 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M1 6 Q4 2 7 6 Q10 10 13 6 Q16 2 19 6" />
                              </svg>
                              Serpentine 3
                            </span>
                          </SelectItem>
                          <SelectItem value="serpentine4" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-4" viewBox="0 0 24 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M1 6 Q4 2 7 6 Q10 10 13 6 Q16 2 19 6 Q22 10 23 6" />
                              </svg>
                              Serpentine 4
                            </span>
                          </SelectItem>
                          <SelectItem value="serpentine5" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-4" viewBox="0 0 28 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M1 6 Q4 2 7 6 Q10 10 13 6 Q16 2 19 6 Q22 10 25 6 Q27 4 28 6" />
                              </svg>
                              Serpentine 5
                            </span>
                          </SelectItem>
                          <SelectItem value="serpentine6" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-4" viewBox="0 0 32 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M1 6 Q4 2 7 6 Q10 10 13 6 Q16 2 19 6 Q22 10 25 6 Q28 2 31 6" />
                              </svg>
                              Serpentine 6
                            </span>
                          </SelectItem>
                          <SelectItem value="serpentine7" className="admin-text">
                            <span className="flex items-center gap-2">
                              <svg className="w-5 h-4" viewBox="0 0 36 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M1 6 Q4 2 7 6 Q10 10 13 6 Q16 2 19 6 Q22 10 25 6 Q28 2 31 6 Q34 10 35 6" />
                              </svg>
                              Serpentine 7
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="admin-text-secondary">{t('capacity')}</Label>
                      <Input
                        type="number"
                        value={tableForm.capacity}
                        onChange={(e) => setTableForm({ ...tableForm, capacity: e.target.value })}
                        className="admin-input"
                        placeholder="10"
                        data-testid="table-capacity-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="admin-text-secondary flex items-center gap-2">
                        A few words for this table
                        <span className="text-xs opacity-70 font-normal">(optional)</span>
                      </Label>
                      <div className="flex gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => setTableNoteMode('write')}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            tableNoteMode === 'write'
                              ? 'bg-[var(--admin-accent)] text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                          }`}
                          data-testid="table-note-mode-write"
                        >
                          Write it
                        </button>
                        <button
                          type="button"
                          onClick={() => setTableNoteMode('say')}
                          className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            tableNoteMode === 'say'
                              ? 'bg-[var(--admin-accent)] text-white'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                          }`}
                          data-testid="table-note-mode-say"
                        >
                          Say it
                        </button>
                      </div>

                      {tableNoteMode === 'write' ? (
                        <>
                          <textarea
                            value={tableForm.table_message}
                            onChange={(e) => setTableForm({ ...tableForm, table_message: e.target.value })}
                            className="w-full min-h-[60px] px-3 py-2 admin-input rounded-md placeholder:opacity-50 focus:outline-none focus:ring-2 focus:ring-[var(--admin-accent)]/50 text-sm"
                            placeholder="Stay awhile. Smile a lot :)"
                            data-testid="table-message-input"
                          />
                          <p className="text-xs admin-text-secondary">Shown to all guests at this table.</p>
                        </>
                      ) : (
                        <TableVoiceRecorder
                          tableForm={tableForm}
                          setTableForm={setTableForm}
                          editingTable={editingTable}
                          eventId={eventId}
                          isRecording={tableIsRecording}
                          setIsRecording={setTableIsRecording}
                          recordedBlob={tableRecordedBlob}
                          setRecordedBlob={setTableRecordedBlob}
                          recordingTime={tableRecordingTime}
                          setRecordingTime={setTableRecordingTime}
                          isUploadingVoice={tableIsUploadingVoice}
                          setIsUploadingVoice={setTableIsUploadingVoice}
                          mediaRecorderRef={tableMediaRecorderRef}
                          recordingTimerRef={tableRecordingTimerRef}
                          chunksRef={tableChunksRef}
                        />
                      )}
                    </div>
                    <Button type="submit" className="w-full admin-accent-bg" data-testid="save-table-btn">
                      <Save className="h-4 w-4 mr-2" />
                      {t('save')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tables.length === 0 ? (
                <div className="col-span-full text-center py-12 text-slate-500">
                  No tables yet. Add your first table!
                </div>
              ) : (
                tables.map((table) => {
                  const tableGuests = guests.filter(g => g.table_id === table.id);
                  // Debug logging for table-guest sync issue
                  if (tableGuests.length === 0 && guests.some(g => g.table_name === table.table_name)) {
                    console.warn(`[TABLE-GUEST SYNC] Table "${table.table_name}" (id: ${table.id}) has 0 guests but there are guests with table_name="${table.table_name}"`);
                    const mismatched = guests.filter(g => g.table_name === table.table_name);
                    console.warn(`[TABLE-GUEST SYNC] Mismatched guests:`, mismatched.map(g => ({ name: g.full_name, table_id: g.table_id, table_name: g.table_name })));
                  }
                  const isGrouped = table.shape === 'grouped';
                  return (
                    <div key={table.id} className={`glass-card p-5 ${isGrouped ? 'border-l-4 border-l-purple-500' : ''}`} data-testid={`table-card-${table.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-mono text-xl text-[#D4AF37]">{table.table_name}</h3>
                          {isGrouped && (
                            <span className="text-xs text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded">
                              Grouped ({table.grouped_piece_ids?.length || 0} pieces)
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditTable(table)}
                            className="text-slate-400 hover:text-white h-8 w-8 p-0"
                            data-testid={`edit-table-${table.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTable(table.id)}
                            className="text-slate-400 hover:text-red-500 h-8 w-8 p-0"
                            data-testid={`delete-table-${table.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-slate-400 text-sm mb-3">
                        {tableGuests.length}{table.capacity ? ` / ${table.capacity}` : ''} {t('guests')}
                      </div>
                      <div className="space-y-1">
                        {tableGuests.slice(0, 5).map(g => (
                          <div key={g.id} className="text-sm text-slate-300 truncate">{g.full_name}</div>
                        ))}
                        {tableGuests.length > 5 && (
                          <div className="text-sm text-slate-500">+{tableGuests.length - 5} more</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Floor Plan Tab */}
          <TabsContent value="floorplan" className="space-y-4">
            <DndProvider backend={HTML5Backend}>
              {/* Custom Drag Layer for visual feedback during drag */}
              <CustomDragLayer zoom={1} />
              
              {/* Premium Banner for Free Tier */}
              {!isPaidTier && (
                <div className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Crown className="h-5 w-5 text-amber-400" />
                    <div>
                      <p className={`font-medium ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>Premium Feature Preview</p>
                      <p className={`text-sm ${adminDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>You can explore the Floor Plan editor. Upgrade to save your layout.</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/subscription?event_id=${eventId}`)}
                    size="sm"
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600"
                  >
                    Upgrade
                  </Button>
                </div>
              )}
              
              {/* Header with Preview Button */}
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="text-lg font-semibold text-white">Floor Plan Editor</h2>
                  <p className="text-sm text-slate-400">Drag and arrange tables on the canvas</p>
                </div>
                <div className="flex items-center gap-2">
                  {/* Generate Floor Plan Button - shows if there are unplaced tables */}
                  {tables.some(t => t.position_x === null || t.position_y === null) && (
                    <Button
                      onClick={handleGenerateFloorPlan}
                      disabled={autoLayoutLoading}
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid="generate-floor-plan-btn"
                    >
                      {autoLayoutLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      Generate tables on floor plan
                    </Button>
                  )}
                  <FloorPlanStylePanel 
                    style={event?.floor_plan_style}
                    onStyleChange={handleFloorPlanStyleChange}
                    adminDarkMode={adminDarkMode}
                  />
                  <Button
                    onClick={() => setShowFloorPlanPreview(true)}
                    variant="outline"
                    className={adminDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}
                    data-testid="preview-floor-plan-btn"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Guest Preview
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Sidebar - Takes 1 column (LEFT side) */}
                <div className="space-y-4 order-2 lg:order-1">
                  {selectedFloorPlanTable ? (
                    <TablePropertiesPanel
                      table={tables.find(t => t.id === selectedFloorPlanTable)}
                      guests={guests}
                      onUpdate={handleUpdateTablePosition}
                      onDelete={handleDeleteFloorPlanTable}
                      onClose={() => setSelectedFloorPlanTable(null)}
                    />
                  ) : selectedCustomPiece ? (
                    <CustomPiecePropertiesPanel
                      piece={customPieces.find(p => p.id === selectedCustomPiece)}
                      onUpdate={handleUpdateCustomPiece}
                      onDelete={handleDeleteCustomPiece}
                      onClose={() => setSelectedCustomPiece(null)}
                    />
                  ) : selectedTextBox ? (
                    <TextBoxPropertiesPanel
                      textBox={textBoxes.find(tb => tb.id === selectedTextBox)}
                      onUpdate={handleUpdateTextBox}
                      onDelete={handleDeleteTextBox}
                      onClose={() => setSelectedTextBox(null)}
                    />
                  ) : (
                    <TableLibrary
                      tables={tables}
                      onAddTable={handleAddFloorPlanTable}
                      onAddVenueElement={handleAddVenueElement}
                      onAddCustomPiece={handleAddCustomPiece}
                    />
                  )}
                </div>
                
                {/* Canvas Area - Takes 3 columns (RIGHT side) */}
                <div className="lg:col-span-3 order-1 lg:order-2">
                  <FloorPlanCanvas
                    tables={tables}
                    guests={guests}
                    venueElements={venueElements}
                    customPieces={customPieces}
                    onUpdateTable={handleUpdateTablePosition}
                    onDeleteTable={handleDeleteFloorPlanTable}
                    onDuplicateTable={handleDuplicateTable}
                    onUpdateVenueElement={handleUpdateVenueElement}
                    onDeleteVenueElement={handleDeleteVenueElement}
                    onDuplicateVenueElement={handleDuplicateVenueElement}
                    onAddCustomPiece={handleAddCustomPiece}
                    onUpdateCustomPiece={handleUpdateCustomPiece}
                    onDeleteCustomPiece={handleDeleteCustomPiece}
                    onDuplicateCustomPiece={handleDuplicateCustomPiece}
                    onGroupPieces={handleGroupPieces}
                    textBoxes={textBoxes}
                    onAddTextBox={handleAddTextBox}
                    onUpdateTextBox={handleUpdateTextBox}
                    onDeleteTextBox={handleDeleteTextBox}
                    selectedTextBoxId={selectedTextBox}
                    onTextBoxSelect={setSelectedTextBox}
                    onSaveLayout={handleSaveFloorPlan}
                    onAutoLayout={handleAutoLayout}
                    saving={savingFloorPlan}
                    autoLayoutLoading={autoLayoutLoading}
                    backgroundUrl={event?.floor_plan_background_url}
                    backgroundOpacity={event?.floor_plan_background_opacity || 50}
                    onUploadBackground={handleUploadFloorPlanBackground}
                    onRemoveBackground={handleRemoveFloorPlanBackground}
                    onOpacityChange={handleFloorPlanOpacityChange}
                    uploadingBackground={uploadingFloorPlanBg}
                    selectedTableId={selectedFloorPlanTable}
                    selectedElementId={selectedVenueElement}
                    selectedPieceId={selectedCustomPiece}
                    floorPlanStyle={event?.floor_plan_style}
                    onTableSelect={(id) => {
                      setSelectedFloorPlanTable(id);
                      setSelectedVenueElement(null);
                      setSelectedCustomPiece(null);
                      setSelectedTextBox(null);
                    }}
                    onElementSelect={(id) => {
                      setSelectedVenueElement(id);
                      // Only clear table selection if we're actually selecting an element
                      if (id) {
                        setSelectedFloorPlanTable(null);
                      }
                      setSelectedCustomPiece(null);
                      setSelectedTextBox(null);
                    }}
                    onPieceSelect={(id) => {
                      setSelectedCustomPiece(id);
                      // Only clear table selection if we're actually selecting a piece
                      if (id) {
                        setSelectedFloorPlanTable(null);
                      }
                      setSelectedVenueElement(null);
                      setSelectedTextBox(null);
                    }}
                    onAssignGuest={handleQuickAssignGuest}
                    onRemoveGuestFromSeat={handleRemoveGuestFromSeat}
                    onSwapGuests={handleSwapGuests}
                    onMoveGroupedTable={handleMoveGroupedTable}
                    onUndo={handleUndo}
                    onRedo={handleRedo}
                    canUndo={undoHistory.length > 0}
                    canRedo={redoHistory.length > 0}
                    finalFloorPlanUrl={event?.final_floor_plan_url}
                    onUploadFinalFloorPlan={handleUploadFinalFloorPlan}
                    onRemoveFinalFloorPlan={handleRemoveFinalFloorPlan}
                    uploadingFinalFloorPlan={uploadingFinalFloorPlan}
                  />
                </div>
              </div>
              
              {/* Floor Plan Info */}
              <div className="glass-card p-4 mt-4">
                <h3 className="text-sm font-semibold text-white mb-2">How to use the Floor Plan</h3>
                <ul className="text-xs text-slate-400 space-y-1">
                  <li>• Drag table shapes or venue elements from the library onto the canvas</li>
                  <li>• Move existing tables by dragging them around</li>
                  <li>• Click a table to select it and edit its properties</li>
                  <li>• Resize venue elements by dragging the corner handles</li>
                  <li>• Use the grid for precise alignment</li>
                  <li>• Press Delete to remove a selected item from the layout</li>
                  <li>• Press Ctrl+Z to undo, Ctrl+Shift+Z to redo</li>
                  <li>• Click "Save Layout" to persist your changes</li>
                  <li>• Click "Guest Preview" to see how guests will view the floor plan</li>
                  <li>• <span className="text-blue-400">Custom Pieces:</span> Drag pieces to canvas, Shift+Click to select multiple, then click "Group" to create a modular table</li>
                </ul>
              </div>
              
              {/* Floor Plan Preview Modal */}
              <FloorPlanPreview
                isOpen={showFloorPlanPreview}
                onClose={() => setShowFloorPlanPreview(false)}
                tables={tables}
                guests={guests}
                eventName={event?.name}
                eventLocation={event?.location}
                venueElements={venueElements}
                customPieces={customPieces}
                floorPlanStyle={event?.floor_plan_style}
              />
            </DndProvider>
          </TabsContent>

          {/* Wishes / Love Gallery Tab */}
          <TabsContent value="wishes" className="space-y-4">
            {/* Premium Banner for Free Tier */}
            {!isPaidTier && (
              <div className="bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/30 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crown className="h-5 w-5 text-pink-400" />
                  <div>
                    <p className={`font-medium ${adminDarkMode ? 'text-white' : 'text-slate-800'}`}>Premium Feature Preview</p>
                    <p className={`text-sm ${adminDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>You can explore the Love Gallery. Upgrade to enable guest wishes.</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/subscription?event_id=${eventId}`)}
                  size="sm"
                  className="bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600"
                >
                  Upgrade
                </Button>
              </div>
            )}
            <div 
              className={`p-6 rounded-xl border ${adminDarkMode ? 'glass-card-dark' : 'bg-white border-slate-200'}`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-gradient-to-br from-pink-500/20 to-amber-500/20">
                  <Heart className="h-6 w-6 text-pink-400" />
                </div>
                <div>
                  <h2 
                    className="text-xl font-semibold"
                    style={th.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                  >
                    Love Gallery
                  </h2>
                  <p 
                    className="text-sm"
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
                  >
                    Hearts and messages from your guests will appear here
                  </p>
                </div>
              </div>
              <LoveGallery 
                eventId={eventId} 
                eventName={event?.name}
                eventDate={event?.date}
                eventLocation={event?.location}
                token={localStorage.getItem('token')} 
              />
            </div>
          </TabsContent>

          {/* Moments Tab */}
          <TabsContent value="moments" className="space-y-4">
            <MomentsGallery eventId={eventId} token={localStorage.getItem('token')} adminDarkMode={adminDarkMode} th={th} />
          </TabsContent>

          {/* Collected Moments Tab — by-person view (Admin Only / BETA) */}
          {isAdmin && (
            <TabsContent value="collected" className="space-y-4">
              <CollectedMomentsView
                eventId={eventId}
                token={localStorage.getItem('token')}
                adminDarkMode={adminDarkMode}
                autoOpenAdd={collectedAutoOpenAdd}
                onAutoOpenConsumed={() => setCollectedAutoOpenAdd(false)}
              />
            </TabsContent>
          )}

          {/* Reveal Tab - Pro/Premium only (free users see paywall) */}
          <TabsContent value="reveal" className="space-y-4">
            {isPaidTier || isAdmin ? (
              <RevealTab
                eventId={eventId}
                token={localStorage.getItem('token')}
                adminDarkMode={adminDarkMode}
                onAddForGuest={() => { setActiveTab('collected'); setCollectedAutoOpenAdd(true); }}
              />
            ) : (
              <div className={`rounded-2xl border ${adminDarkMode ? 'border-slate-800 bg-slate-900/40' : 'border-slate-200 bg-slate-50'} p-10 text-center`}>
                <div className="max-w-lg mx-auto space-y-5">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15 text-amber-500">
                    <Sun className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-light mb-2">The Morning After Reveal</h3>
                    <p className="text-sm opacity-70 leading-relaxed">
                      A cinematic, private webpage that arrives in the couple&rsquo;s inbox the morning
                      after the celebration. Photos, voice notes and wishes their guests left for them &mdash;
                      curated, sequenced, and printable as an editorial book.
                    </p>
                  </div>
                  <div className={`rounded-xl ${adminDarkMode ? 'bg-slate-800/60' : 'bg-white'} p-5 text-left space-y-2`}>
                    <p className="text-xs uppercase tracking-wider opacity-60">Included in</p>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-600">
                        <Crown className="h-3 w-3" /> Pro
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-500/15 text-purple-600">
                        <Crown className="h-3 w-3" /> Premium
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setActiveTab('upgrade')}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid="reveal-upgrade-cta"
                  >
                    <Crown className="h-4 w-4 mr-2" />
                    Unlock the Reveal
                  </Button>
                  <p className="text-xs opacity-50">
                    You can preview this tab anytime. Upgrade to activate it for your event.
                  </p>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Digital Invitation Tab - Admin Only */}
          {isAdmin && (
            <TabsContent value="rsvp" className="space-y-4">
              {rsvpPageInfo?.engine === 'v2' ? (
                /* ─── V2 Engine Active ─────────────────── */
                <div className="space-y-4">
                  <div className={`p-6 rounded-xl border ${adminDarkMode ? 'border-emerald-500/30 bg-emerald-900/10' : 'border-emerald-200 bg-emerald-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                          <h3 className={`text-sm font-semibold ${adminDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                            Using New Invitation Builder
                          </h3>
                        </div>
                        <p className={`text-xs mt-1 ${adminDarkMode ? 'text-emerald-400/70' : 'text-emerald-700/70'}`}>
                          Template: <span className="font-medium">{rsvpPageInfo.v2_template_id?.replace(/-/g, ' ').replace(/\bv1$/i, '').trim()}</span>
                          {rsvpPageInfo.is_published ? ' · Published' : ' · Draft'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/events/${eventId}/template-picker`)}
                          className={`px-4 py-2 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${adminDarkMode ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-900/20' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          data-testid="v2-change-template-btn"
                        >
                          Change Template
                        </button>
                        <button
                          onClick={() => navigate(`/events/${eventId}/invitation-editor`)}
                          className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors whitespace-nowrap"
                          data-testid="v2-open-editor-btn"
                        >
                          Open Editor
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* ─── Mobile QA ─────────────────────────────────────
                      Preview on phone · Check mobile layout · Mobile-parity
                      layout opt-in. (Phase 2.3 Lite — section label.) */}
                  <div data-testid="event-setup-section-mobile-qa">
                    <h4 className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${adminDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Mobile QA
                    </h4>
                    <LayoutVersionPanel
                      eventId={eventId}
                      rsvpPageInfo={rsvpPageInfo}
                      onUpdated={(res) => {
                        setRsvpPageInfo(prev => ({
                          ...prev,
                          layout_version: res.layout_version,
                          v2_content_backup_at: res.backup_at ?? prev?.v2_content_backup_at,
                        }));
                      }}
                      adminDarkMode={adminDarkMode}
                    />
                  </div>

                  {/* ─── Performance ─────────────────────────────────────
                      Studio-level "Optimize all images" maintenance action.
                      Proactive companion to the Mobile Layout Check's
                      contextual Optimize for mobile button. */}
                  <div data-testid="event-setup-section-performance">
                    <OptimizeAllImagesPanel
                      eventId={eventId}
                      adminDarkMode={adminDarkMode}
                    />
                  </div>

                  {/* ─── Client handoff ────────────────────────────────
                      Private review link for planner / couple approval.
                      Phase 2.2 Lite + Phase 2.2.1 reviewer-note display. */}
                  <div data-testid="event-setup-section-client-handoff">
                    <h4 className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${adminDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Client handoff
                    </h4>
                    <ReviewLinkPanel
                      eventId={eventId}
                      adminDarkMode={adminDarkMode}
                    />
                  </div>

                  {/* ─── Guest link ────────────────────────────────────
                      Real guest invitation URL. Phase 2.3 Lite — explicit
                      Copy / Open with a Draft badge when not published. */}
                  <div data-testid="event-setup-section-guest-link">
                    <h4 className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${adminDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Guest link
                    </h4>
                    <EventLinksPanel rsvpPageInfo={rsvpPageInfo} />
                  </div>

                  {/* Option to switch back to classic */}
                  <div className={`p-4 rounded-xl border ${adminDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`text-xs font-medium ${adminDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Classic Editor</h4>
                        <p className={`text-xs mt-0.5 ${adminDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Switch back to the original template editor</p>
                      </div>
                      <button
                        onClick={async () => {
                          if (window.confirm('Switch to Classic Editor? Your V2 template data will be preserved, but the public URL will show the classic template instead.')) {
                            try {
                              await axios.put(`${API}/events/${eventId}/rsvp-page`, {
                                engine: 'v1',
                              }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                              setRsvpPageInfo(prev => ({ ...prev, engine: 'v1' }));
                              toast.success('Switched to Classic Editor');
                            } catch {
                              toast.error('Failed to switch editor');
                            }
                          }
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${adminDarkMode ? 'border-gray-600 text-gray-400 hover:text-gray-300 hover:border-gray-500' : 'border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400'}`}
                        data-testid="switch-to-v1-btn"
                      >
                        Switch to Classic
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* ─── V1 Engine / No RSVP Page ─────────── */
                <>
                  {/* V2 upgrade banner */}
                  <div className={`p-5 rounded-xl border ${adminDarkMode ? 'border-amber-500/30 bg-amber-900/10' : 'border-amber-200 bg-amber-50'} flex items-center justify-between`}>
                    <div>
                      <h3 className={`text-sm font-semibold ${adminDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>New Invitation Builder</h3>
                      <p className={`text-xs mt-1 ${adminDarkMode ? 'text-amber-400/70' : 'text-amber-700/70'}`}>Choose from beautiful templates and customize with the new visual editor</p>
                    </div>
                    <button
                      onClick={() => navigate(`/events/${eventId}/template-picker`)}
                      className="px-4 py-2 rounded-lg text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 transition-colors whitespace-nowrap"
                      data-testid="v2-editor-btn"
                    >
                      Try New Builder
                    </button>
                  </div>

                  {/* Classic V1 Editor */}
                  <RSVPEditorTab 
                    eventId={eventId}
                    event={event}
                    token={localStorage.getItem('token')}
                  />
                </>
              )}
            </TabsContent>
          )}

          {/* RSVP Dashboard Tab */}
          <TabsContent value="rsvp-dashboard" className="space-y-4" data-testid="rsvp-dashboard-tab">
            <RSVPDashboard eventId={eventId} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div 
              className={`p-6 max-w-2xl rounded-xl border ${adminDarkMode ? 'glass-card-dark' : 'bg-white border-slate-200'}`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <form onSubmit={handleUpdateEvent} className="space-y-6">
                <div className="space-y-2">
                  <Label 
                    className=""
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#64748b' }}
                  >
                    {t('eventName')}
                  </Label>
                  <Input
                    value={event?.name || ''}
                    onChange={(e) => setEvent({ ...event, name: e.target.value })}
                    className={adminDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    required
                    data-testid="settings-event-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label 
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#64748b' }}
                  >
                    {t('eventDate')}
                  </Label>
                  <Input
                    type="date"
                    value={event?.date || ''}
                    onChange={(e) => setEvent({ ...event, date: e.target.value })}
                    className={adminDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    required
                    data-testid="settings-event-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label 
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#64748b' }}
                  >
                    {t('eventLocation')}
                  </Label>
                  <Input
                    value={event?.location || ''}
                    onChange={(e) => setEvent({ ...event, location: e.target.value })}
                    className={adminDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    required
                    data-testid="settings-event-location"
                  />
                </div>
                
                {/* Event Message - Shown to all guests */}
                <div className="space-y-2">
                  <Label 
                    className="flex items-center gap-2"
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#64748b' }}
                  >
                    💌 Event Message
                    <span 
                      className="text-xs font-normal"
                      style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: '#64748b' }}
                    >
                      (shown to all guests)
                    </span>
                    {!isPaidTier && <span className="text-xs text-amber-500 font-medium ml-2">Premium</span>}
                  </Label>
                  <textarea
                    value={event?.event_message || ''}
                    onChange={(e) => setEvent({ ...event, event_message: e.target.value })}
                    className={`w-full min-h-[80px] px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 ${adminDarkMode ? 'bg-slate-800/50 border border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400'}`}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    placeholder="Welcome to our event! 🎉 Dinner starts at 7 PM..."
                    data-testid="settings-event-message"
                  />
                  <p 
                    className="text-xs"
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: '#64748b' }}
                  >
                    Supports emojis! This message will appear on the guest search page.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label 
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#64748b' }}
                  >
                    {t('language')}
                  </Label>
                  <Select value={event?.language || 'en'} onValueChange={(val) => setEvent({ ...event, language: val })}>
                    <SelectTrigger 
                      className={adminDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}
                      style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                      data-testid="settings-language-select"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">{t('english')}</SelectItem>
                      <SelectItem value="pt">{t('portuguese')}</SelectItem>
                      <SelectItem value="fr">{t('french')}</SelectItem>
                      <SelectItem value="es">{t('spanish')}</SelectItem>
                      <SelectItem value="el">{t('greek')}</SelectItem>
                      <SelectItem value="ru">{t('russian')}</SelectItem>
                      <SelectItem value="it">{t('italian')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Color Theme Section */}
                <div className="space-y-4 p-4 rounded-xl admin-card">
                  <Label className="flex items-center gap-2 text-base admin-text-secondary">
                    <Palette className="h-5 w-5" />
                    Public Event Color Theme
                    {!isPaidTier && <span className="text-xs text-amber-500 font-medium ml-2">Premium</span>}
                  </Label>
                  
                  {/* Theme Mode Selector */}
                  <div className="space-y-2">
                    <Label className="text-sm admin-text-secondary">
                      Theme Mode
                    </Label>
                    <Select 
                      value={event?.theme_mode || 'preset'} 
                      onValueChange={handleThemeModeChange}
                    >
                      <SelectTrigger className="admin-input" data-testid="theme-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="admin-card">
                        <SelectItem value="preset">Preset Themes</SelectItem>
                        <SelectItem value="customization">Customization</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Preset Theme Selector - Show only in preset mode */}
                  {event?.theme_mode !== 'customization' && (
                    <div className="space-y-2">
                      <Label className="admin-text-secondary text-sm">Select Preset</Label>
                      <div className="flex gap-2">
                        <Select 
                          value={event?.color_theme || 'coastalSilk'} 
                          onValueChange={handleThemeChange}
                        >
                          <SelectTrigger className="admin-input flex-1" data-testid="theme-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="admin-card">
                            {themeOptions.map((theme) => (
                              <SelectItem key={theme.id} value={theme.id}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-4 h-4 rounded-full border admin-border"
                                    style={{ backgroundColor: getTheme(theme.id).colors.primary }}
                                  />
                                  {theme.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={openThemePreview}
                          className="admin-border admin-text hover:opacity-80"
                          data-testid="preview-theme-btn"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </Button>
                      </div>
                      
                      {/* Theme Color Preview Swatches */}
                      {event?.color_theme && (
                        <div className="flex gap-2 mt-2">
                          {['background', 'primary', 'accent', 'tableButton'].map((colorKey) => (
                            <div 
                              key={colorKey}
                              className="w-8 h-8 rounded-lg border admin-border shadow-sm"
                              style={{ backgroundColor: getTheme(event.color_theme).colors[colorKey] }}
                              title={colorKey}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
              
                  {/* Customization Mode UI */}
                  {event?.theme_mode === 'customization' && (
                    <div className="space-y-4">
                      {/* Color Pickers Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ColorPicker
                          label="Background Color"
                          value={customColors.background}
                          onChange={(val) => handleCustomColorChange('background', val)}
                          defaultValue={defaultCustomColors.background}
                        />
                        
                        <ColorPicker
                          label="Primary Color"
                          value={customColors.primary}
                          onChange={(val) => handleCustomColorChange('primary', val)}
                          defaultValue={defaultCustomColors.primary}
                        />
                        
                        <ColorPicker
                          label="Accent Color"
                          value={customColors.accent}
                          onChange={(val) => handleCustomColorChange('accent', val)}
                          defaultValue={defaultCustomColors.accent}
                        />
                        
                        <ColorPicker
                          label="Table Button Color"
                          value={customColors.tableButton}
                          onChange={(val) => handleCustomColorChange('tableButton', val)}
                          defaultValue={defaultCustomColors.tableButton}
                        />
                        
                        <ColorPicker
                          label="Text Color"
                          value={customColors.text}
                          onChange={(val) => handleCustomColorChange('text', val)}
                          defaultValue={defaultCustomColors.text}
                        />
                        
                        <ColorPicker
                          label="Text Secondary Color"
                          value={customColors.textSecondary}
                          onChange={(val) => handleCustomColorChange('textSecondary', val)}
                          defaultValue={defaultCustomColors.textSecondary}
                        />
                      </div>
                      
                      {/* Live Preview Swatches */}
                      <div className="flex items-center gap-3">
                        <span className="text-xs admin-text-secondary">Current:</span>
                        <div className="flex gap-1">
                          {['background', 'primary', 'accent', 'tableButton', 'text', 'textSecondary'].map((key) => (
                            <div 
                              key={key}
                              className="w-6 h-6 rounded border admin-border"
                              style={{ backgroundColor: customColors[key] }}
                              title={key}
                            />
                          ))}
                        </div>
                      </div>
                      
                      {/* Contrast Warning */}
                      {customColors.primary && customColors.background && 
                       !hasGoodContrast(customColors.primary, customColors.background) && (
                        <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                          <span>Primary color has low contrast with background. Text may be hard to read.</span>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setPreviewTheme('custom');
                            setShowThemePreview(true);
                          }}
                          className="admin-border admin-text hover:opacity-80"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Preview Custom Theme
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={resetAllCustomColors}
                          className="admin-border admin-text hover:opacity-80"
                        >
                          <RotateCcw className="h-4 w-4 mr-2" />
                          Reset All Colors
                        </Button>
                      </div>
                      
                      {/* Background Image Section */}
                      <div className="border-t admin-border pt-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <Label className="admin-text-secondary flex items-center gap-2">
                            <Image className="h-4 w-4" />
                            Background Image
                          </Label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useBackgroundImage}
                              onChange={(e) => setUseBackgroundImage(e.target.checked)}
                              className="rounded admin-border"
                            />
                            <span className="text-sm admin-text-secondary">Use background image</span>
                          </label>
                        </div>
                        
                        {useBackgroundImage && (
                          <div className="space-y-3">
                            {/* Image Preview */}
                            {event?.background_image_url && (
                              <div className="relative w-full h-40 rounded-lg overflow-hidden border admin-border">
                                <img 
                                  src={`${process.env.REACT_APP_BACKEND_URL}${event.background_image_url}`}
                                  alt="Background preview"
                                  className="w-full h-full object-cover"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  onClick={handleRemoveBackgroundImage}
                                  className="absolute top-2 right-2"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                            
                            {/* Upload Button */}
                            <div>
                              <input
                                type="file"
                                ref={bgImageInputRef}
                                onChange={handleBackgroundImageUpload}
                                accept="image/png,image/jpeg,image/jpg,image/webp"
                                className="hidden"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => bgImageInputRef.current?.click()}
                                disabled={uploadingBgImage}
                                className="admin-border admin-text hover:opacity-80"
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                {uploadingBgImage ? 'Uploading...' : (event?.background_image_url ? 'Replace Image' : 'Upload Image')}
                              </Button>
                            </div>
                            
                            {/* Image Size Guidance */}
                            <div className="text-xs admin-text-secondary space-y-1 admin-card p-3 rounded-lg">
                              <p className="font-medium admin-text-secondary">📱 Mobile Background Guidelines:</p>
                              <ul className="list-disc list-inside space-y-0.5">
                                <li>Recommended size: <span className="admin-text">1080 × 1920 px</span> (9:16)</li>
                                <li>Keep key content centered in the middle 70%</li>
                                <li>Avoid text near edges</li>
                                <li>Use compressed JPG/WEBP for best performance</li>
                                <li>Maximum file size: 10MB</li>
                              </ul>
                            </div>
                            
                            {/* Overlay Intensity */}
                            {event?.background_image_url && (
                              <div className="space-y-2">
                                <Label className="admin-text-secondary text-sm flex items-center justify-between">
                                  <span>Dark Overlay Intensity</span>
                                  <span className="admin-text-secondary">{event?.overlay_intensity || 50}%</span>
                                </Label>
                                <Slider
                                  value={[event?.overlay_intensity || 50]}
                                  onValueChange={([val]) => setEvent(prev => ({ ...prev, overlay_intensity: val }))}
                                  min={0}
                                  max={90}
                                  step={5}
                                  className="w-full"
                                />
                                <p className="text-xs admin-text-secondary">Adjust overlay to improve text readability</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Broadcast Settings Section */}
                <div className="border-t admin-border pt-6 mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <MessageSquare className="h-5 w-5 admin-icon" />
                    <h3 className="font-heading text-lg font-semibold admin-text">Broadcast Settings</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Push Notifications Toggle - DISABLED FOR NOW */}
                    {/* 
                    <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-3">
                        <BellRing className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="text-white font-medium">Push Notifications</p>
                          <p className="text-sm text-slate-400">
                            Guests can receive announcements even when the page is closed
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={pushNotificationsEnabled}
                        onCheckedChange={handlePushToggle}
                        disabled={savingPushSettings}
                        data-testid="push-notifications-toggle"
                      />
                    </div>
                    */}
                    
                    {/* Broadcast Panel Link */}
                    <div className="p-4 admin-card rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="admin-text font-medium">Broadcast Panel</p>
                          <p className="text-sm admin-text-secondary">
                            Send real-time announcements to all guests
                          </p>
                        </div>
                        <Button
                          onClick={() => navigate(`/event/${event?.slug}/broadcast`)}
                          className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
                          data-testid="open-broadcast-panel-btn"
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Open Broadcast Panel
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button type="submit" className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]" data-testid="save-event-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {t('save')}
                  </Button>
                  <Button type="button" variant="destructive" onClick={handleDeleteEvent} data-testid="delete-event-btn">
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('delete')}
                  </Button>
                </div>
              </form>
            </div>
          </TabsContent>

          {/* Upgrade Tab */}
          <TabsContent value="upgrade">
            <div className="glass-card p-6 max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <Crown className="h-6 w-6 admin-icon" />
                <h2 className="font-heading text-xl font-semibold admin-text">{t('upgradeEvent')}</h2>
              </div>
              
              {tierInfo && (
                <div className="mb-6 p-4 admin-card rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="admin-text-secondary text-sm">{t('currentPlan')}</p>
                      <p className="admin-text font-medium text-lg">{tierInfo.tier_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="admin-text-secondary text-sm">{t('guests')}</p>
                      <p className="admin-text font-medium">
                        {tierInfo.guest_count} / {tierInfo.max_guests === -1 ? '∞' : tierInfo.max_guests}
                      </p>
                    </div>
                    {tierInfo.is_trial && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-amber-500/20 rounded-full">
                        <Clock className="h-4 w-4 text-amber-400" />
                        <span className="text-amber-400 text-sm">{t('trialActive')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-3 gap-4">
                {pricingTiers.filter(tier => tier.id !== 'free').map((tier) => {
                  const isCurrentTier = tierInfo?.current_tier === tier.id;
                  
                  const tierColors = {
                    premium: 'border-blue-500/50 bg-blue-500/5',
                    pro: 'border-amber-500/50 bg-amber-500/5'
                  };
                  
                  const getTierNameLocal = (tierId) => {
                    switch (tierId) {
                      case 'premium': return t('premium');
                      case 'pro': return t('pro');
                      default: return tierId;
                    }
                  };
                  
                  return (
                    <div
                      key={tier.id}
                      className={`rounded-xl border p-5 admin-card ${isCurrentTier ? 'ring-2 ring-green-500' : tierColors[tier.id] || 'admin-border'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold admin-text">{getTierNameLocal(tier.id)}</h3>
                        {isCurrentTier && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                            {t('current')}
                          </span>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <span className="text-2xl font-bold admin-accent">€{tier.price}</span>
                        <span className="admin-text-secondary text-sm ml-1">+ 23% {t('tax')}</span>
                      </div>
                      
                      <ul className="space-y-2 mb-4 text-sm admin-text-secondary">
                        <li>• {tier.max_guests === -1 ? t('unlimited') : tier.max_guests} {t('guests')}</li>
                        <li>• {t('allColorThemes')}</li>
                        <li>• {t('customMessages')}</li>
                        <li>• {t('backgroundImages')}</li>
                        <li>• {t('customQrColor')}</li>
                        <li>• Floor Plan Editor</li>
                        <li>• Love Gallery</li>
                      </ul>
                      
                      <div className="space-y-2">
                        <Button
                          size="sm"
                          className={`w-full ${
                            tier.id === 'premium' ? 'bg-blue-600 hover:bg-blue-700' :
                            'bg-amber-600 hover:bg-amber-700'
                          }`}
                          onClick={() => navigate(`/subscription?event_id=${eventId}`)}
                          disabled={isCurrentTier}
                        >
                          {isCurrentTier ? t('current') : t('upgradeNow')}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 text-center">
                <Button
                  variant="link"
                  onClick={() => navigate(`/subscription?event_id=${eventId}`)}
                  className="text-[#D4AF37]"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t('viewFullPricing')}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Support Contact Tab */}
          <TabsContent value="support">
            <div 
              className={`p-6 max-w-2xl rounded-xl border ${adminDarkMode ? 'glass-card-dark' : 'bg-white border-slate-200'}`}
              style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border } : {}}
            >
              <div className="flex items-center gap-3 mb-6">
                <SupportIcon className="h-6 w-6" color={th.useInlineStyles ? th.colors.accent : '#D4AF37'} />
                <h2 
                  className="font-heading text-xl font-semibold"
                  style={th.useInlineStyles ? { color: th.colors.text } : { color: adminDarkMode ? 'white' : '#1e293b' }}
                >
                  {t('supportContact')}
                </h2>
              </div>
              
              <p 
                className="mb-6"
                style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#94a3b8' : '#64748b' }}
              >
                {t('supportHelpText')}
              </p>

              <form onSubmit={handleSendSupport} className="space-y-4">
                <div className="space-y-2">
                  <Label 
                    htmlFor="support-subject"
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#64748b' }}
                  >
                    {t('supportSubject')}
                  </Label>
                  <Input
                    id="support-subject"
                    value={supportForm.subject}
                    onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                    placeholder={t('supportSubjectPlaceholder')}
                    className={adminDarkMode ? 'bg-slate-900/50 border-slate-700 focus:border-amber-500/50' : 'bg-white border-slate-200 focus:border-amber-500/50'}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    required
                    data-testid="support-subject-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label 
                    htmlFor="support-message"
                    style={th.useInlineStyles ? { color: th.colors.textSecondary } : { color: adminDarkMode ? '#cbd5e1' : '#64748b' }}
                  >
                    {t('supportMessage')}
                  </Label>
                  <Textarea
                    id="support-message"
                    value={supportForm.message}
                    onChange={(e) => setSupportForm({ ...supportForm, message: e.target.value })}
                    placeholder={t('supportMessagePlaceholder')}
                    className={`min-h-[150px] resize-none ${adminDarkMode ? 'bg-slate-900/50 border-slate-700 focus:border-amber-500/50' : 'bg-white border-slate-200 focus:border-amber-500/50'}`}
                    style={th.useInlineStyles ? { backgroundColor: th.colors.card, borderColor: th.colors.border, color: th.colors.text } : {}}
                    required
                    data-testid="support-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sendingSupport}
                  className="text-white"
                  style={th.useInlineStyles ? { backgroundColor: th.colors.accent } : { backgroundColor: '#D4AF37', color: '#0f172a' }}
                  data-testid="support-send-btn"
                >
                  {sendingSupport ? (
                    <>{t('supportSending')}</>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      {t('supportSendMessage')}
                    </>
                  )}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Theme Preview Dialog */}
      <Dialog open={showThemePreview} onOpenChange={setShowThemePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0 bg-transparent border-none">
          <div className="relative rounded-xl overflow-hidden shadow-2xl">
            {/* Preview Header */}
            <div className="bg-slate-900 p-4 flex items-center justify-between border-b border-slate-700">
              <div className="flex items-center gap-3">
                <Palette className="h-5 w-5 text-[#D4AF37]" />
                <span className="font-semibold text-white">
                  {previewTheme === 'custom' ? 'Custom Theme Preview' : 'Theme Preview'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {previewTheme !== 'custom' && (
                  <Select value={previewTheme || 'coastalSilk'} onValueChange={setPreviewTheme}>
                    <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {themeOptions.map((theme) => (
                        <SelectItem key={theme.id} value={theme.id}>
                          {theme.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button
                  onClick={() => {
                    setEvent({ ...event, color_theme: previewTheme });
                    setShowThemePreview(false);
                    toast.success('Theme applied! Click Save to confirm.');
                  }}
                  className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
                >
                  Apply Theme
                </Button>
                <Button
                  onClick={handleDownloadDesignHTML}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  title="Download design as HTML for motion graphics"
                >
                  <Code className="h-4 w-4 mr-2" />
                  Export HTML
                </Button>
              </div>
            </div>
            
            {/* Preview Content - Simulated Public Page */}
            {previewTheme && (
              <ThemePreviewContent 
                theme={previewTheme === 'custom' ? buildCustomTheme(customColors) : getTheme(previewTheme)} 
                eventName={event?.name || 'Event Name'}
                isCustom={previewTheme === 'custom'}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Required Popup */}
      <Dialog open={showUpgradePopup} onOpenChange={setShowUpgradePopup}>
        <DialogContent className="max-w-md bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-white">
              <div className="p-2 rounded-full bg-amber-500/20">
                <Crown className="h-6 w-6 text-amber-500" />
              </div>
              {t('premiumFeature')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-300">
              {t('premiumFeaturesRequired')}
            </p>
            
            <ul className="space-y-2">
              {pendingPremiumFeatures.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 text-slate-400">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  {feature === 'Custom Event Message' ? t('customEventMessage') :
                   feature === 'Custom Color Theme' ? t('customColorTheme') :
                   feature === 'Custom Color Settings' ? t('customColorSettings') : feature}
                </li>
              ))}
            </ul>
            
            <p className="text-sm text-slate-500">
              {t('upgradeToUnlock')}
            </p>
          </div>
          
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowUpgradePopup(false)}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                setShowUpgradePopup(false);
                // Navigate to upgrade tab
                const upgradeTab = document.querySelector('[value="upgrade"]');
                if (upgradeTab) upgradeTab.click();
              }}
              className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F]"
            >
              <Crown className="h-4 w-4 mr-2" />
              {t('upgradeNow')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Theme Preview Component - Simulates the Public Guest Search Page
function ThemePreviewContent({ theme, eventName, isCustom }) {
  const colors = theme.colors;
  
  return (
    <div 
      className="p-8 min-h-[500px]"
      style={{ backgroundColor: colors.background }}
    >
      {/* Custom Theme Badge */}
      {isCustom && (
        <div className="text-center mb-4">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium" 
            style={{ backgroundColor: `${colors.primary}20`, color: colors.primary }}>
            Custom Theme Preview
          </span>
        </div>
      )}
      
      {/* Simulated Header */}
      <div className="text-center mb-8">
        <h1 
          className="text-3xl font-bold mb-2"
          style={{ color: colors.primary }}
        >
          {eventName}
        </h1>
        <p style={{ color: colors.textSecondary }}>Find Your Table</p>
      </div>

      {/* Simulated Search */}
      <div className="max-w-md mx-auto mb-8">
        <div 
          className="rounded-xl p-4"
          style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}` }}
        >
          <div 
            className="rounded-lg p-3 mb-4"
            style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.border}` }}
          >
            <span style={{ color: colors.textMuted }}>Search your name...</span>
          </div>
        </div>
      </div>

      {/* Simulated Result Card */}
      <div className="max-w-md mx-auto">
        <div 
          className="rounded-xl p-6 text-center"
          style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}` }}
        >
          <h3 
            className="text-xl font-semibold mb-2"
            style={{ color: colors.text }}
          >
            John Smith
          </h3>
          <p 
            className="text-sm mb-4"
            style={{ color: colors.textSecondary }}
          >
            Your assigned table
          </p>
          <button
            className="px-8 py-4 rounded-xl text-2xl font-bold transition-transform hover:scale-105"
            style={{ 
              backgroundColor: colors.tableButton, 
              color: colors.tableButtonText 
            }}
          >
            Table 5
          </button>
        </div>
      </div>

      {/* Color Palette Display */}
      <div className="mt-8 flex justify-center gap-2">
        {Object.entries(colors).slice(0, 6).map(([name, color]) => (
          <div key={name} className="text-center">
            <div 
              className="w-10 h-10 rounded-lg border shadow-sm mx-auto mb-1"
              style={{ backgroundColor: color, borderColor: colors.border }}
            />
            <span className="text-xs" style={{ color: colors.textMuted }}>
              {name.replace(/([A-Z])/g, ' $1').trim()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
